// Marrakech, "Lanterns in Jemaa el-Fnaa": a warm, dry night in early October on Jemaa el-Fnaa. We stand on the east
// side of the square among the food stalls and look west-southwest, the way the smoke drifts: the Koutoubia's minaret
// rises beyond the square's south-west corner, floodlit gold over the palms of its gardens and the long green-tiled
// roofs of its prayer hall, its lantern and the three copper globes on top. Café terraces with striped awnings and
// string lights stand on the square's pink-ochre houses to the left and right (a rooftop sign for an invented mint-tea
// house on the right-hand one is the city's billboard). In front of the minaret the far row of food stalls sends up
// its smoke; nearer, two stalls (grills on the left, snails on the right) with their cooks, diners and bulbs, an
// orange-juice cart, a storyteller with his lamp and his ring of listeners, a gnawa trio (qraqeb and guembri) with
// their audience, a lantern seller's stall of pierced-brass lanterns throwing stars over the ground, walkers, a
// water seller, a calèche and a moped now and then, cats. A clear deep-blue sky, stars and a young crescent moon;
// swifts circle the tower. No rain or snow of its own: grill smoke, embers and dust motes in the lamplight drift at
// three depths instead (a high haze behind the tower, the stalls' plumes and sparks, motes and near embers in front).
//
// Back to front: sky -> stars, moon, high smoke haze -> far city and the Atlas -> the Koutoubia, its swifts ->
// prayer hall, palms -> the houses round the square, their terraces, the billboard -> far stall row -> the square:
// everyone on it sorted by the row of their feet, the near stalls among them (the far lanes pass behind them) -> near
// motes, embers and smoke -> vignette.
//
// The loop's timeline (loop seconds; small things run all loop long):
//   every  8 s   the grill cook fans his coals: a flare, a puff of smoke and a burst of sparks (stall 14)
//   every 12 s   the juice seller presses and hands over a glass (two customers take turns)
//   every ~4 s   the storyteller changes his gesture; the qraqeb click on every beat; the gnawa's tassels turn
//     2 -  14    a cat trots across the near ground
//    14 -  22    a moped crosses left to right (a toot at 15)
//    30 -  62    an aeroplane's lights cross high up, coming in to land
//    38 -  70    a calèche crosses right to left along the far lane
//    58, 118, 178, 232   the storyteller's punchline: his ring laughs and claps
//    72 - 106    the water seller walks in, rings his bell (91, 93), a tourist takes his photo (98), he walks on
//   106 - 114    the moped comes back right to left (a toot at 107)
//   128 - 146    a skein of egrets crosses the sky left to right
//   150 - 200    the gnawa build up: the qraqeb quicken, a dancer steps out and spins his tassel, the ring claps
//   156 - 188    the calèche comes back left to right
//   196 - 228    a couple buys a lantern: the seller lifts one and turns it, its stars sweep the ground, they leave
//   204 - 212    the moped again, left to right (a toot at 205)
//   208 - 226    the egrets fly back right to left
// render(t, { crowd }) thins the square: 1 = everyone, just above 0 = only the cast (cooks, sellers, storyteller,
// musicians, the people of the timed moments), 0 = nobody: the stalls lit and unattended (CROWD below is the default).
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, cycle, hash, rng, bayer, hex,
  makeCanvas, px, rect, ditherFill, makeHalo, drawHalo, text, textWidth, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                             // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const MOON = { x: 150, y: 30, r: 6 };
// The Koutoubia: the shaft's lit east face (x0..x1) and its shaded north face (to sx1), the top of its wall and the
// foot hidden by the prayer hall; the lantern on the terrace; the spire with its three globes (tip row = landmarkTop)
const KT = { x0: 221, x1: 239, sx1: 244, wall: 66, base: 160 };
const LT = { x0: 226, x1: 234, sx1: 237, wall: 42, base: 61 };
const SP = { x: 230, tip: 12 };
const GLOBES = [[28, 2.6], [22, 2.1], [17, 1.5]];    // [centre row, radius], biggest at the bottom
const BB = { x: 276, y: 98, w: 48, h: 20 };          // the rooftop sign's face (exported as `billboard`)
const BB_ROOF = 140;                                 // the roof its scaffold stands on
const FAR = 178;                                     // the far row of stalls stands on this row
const STALL_A = { x0: 14, x1: 112, num: '14' };      // the grill stall
const STALL_B = { x0: 318, x1: 402, num: '31' };     // the snail stall
const POT = 386;                                     // its big pot of snails
const EAVE = 175, COUNTER = 193, FRONT = 200, BENCH = 203;
const LANE_V = 211;                                  // the calèche's and the moped's wheels
const JUICE = { x: 8, y: 240 };                      // the orange-juice cart: left edge, wheels' row
const STORY = { x: 160, y: 228 };                    // the storyteller's feet
const GNAWA = { x: 284, y: 230 };                    // the guembri player's feet
const HENNA = { x: 222, y: 198 };                    // the henna artist under her parasol
const LANT = { x0: 424, x1: 478, bar: 150, bar2: 172, rug: 238 };   // the lantern seller's stall
const personH = y => Math.round(14 + (y - 190) * 0.18);   // how tall a grown-up is with feet on row y
// The share of the square shown by default; render(t, { crowd }) overrides it for one frame. Everyone who can thin
// has a rank in (0, 1) fixed at build time (spread over each group along the golden ratio) and is drawn while
// rank < crowd. CAST: there at every level above 0 and gone at 0 (with what only they explain: the guembri, the
// glasses, the lifted lantern, the ridden calèche and moped). Rank 0: not a person, always drawn.
const CROWD = 0.6;
const CAST = -1;
const spread = i => frac((i + 0.5) * 0.6180339887);
const seen = (rank, crowd) => rank === 0 || (crowd > 0 && rank < crowd);

/* ================= Pixel helpers ================= */
function painter(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data, cache = {};
  const put = (i, y, col) => {
    i = Math.round(i); y = Math.round(y);
    if (i < 0 || y < 0 || i >= w || y >= h || !col) return;
    const rgb = cache[col] || (cache[col] = hex(col)), o = (y * w + i) * 4;
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = 255;
  };
  const has = (i, y) => i >= 0 && y >= 0 && i < w && y < h && d[(y * w + i) * 4 + 3] > 0;
  const get = (i, y) => { const o = (y * w + i) * 4; return '#' + [d[o], d[o + 1], d[o + 2]].map(v => v.toString(16).padStart(2, '0')).join(''); };
  const box = (x0, y0, bw, bh, col) => { for (let y = y0; y < y0 + bh; y++) for (let i = x0; i < x0 + bw; i++) put(i, y, typeof col === 'function' ? col(i, y) : col); };
  return { c, x, w, h, put, has, get, box, done: () => { x.putImageData(img, 0, 0); return c; } };
}
function line(c, x0, y0, x1, y1, col) {               // integer Bresenham line on a 2d context
  x0 = Math.round(x0); y0 = Math.round(y0); x1 = Math.round(x1); y1 = Math.round(y1);
  const dx = Math.abs(x1 - x0), dy = -Math.abs(y1 - y0), sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  c.fillStyle = col;
  for (;;) {
    c.fillRect(x0, y0, 1, 1);
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) { err += dy; x0 += sx; }
    if (e2 <= dx) { err += dx; y0 += sy; }
  }
}
function pline(p, x0, y0, x1, y1, col) {              // the same into a painter
  const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
  for (let k = 0; k <= n; k++) p.put(lerp(x0, x1, k / n), lerp(y0, y1, k / n), typeof col === 'function' ? col(k / n) : col);
}
function sprite(rows, key, flip = false) {            // rows of characters + a colour key ('.' = transparent)
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.put(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
function stamp(p, rows, key, x0, y0) {
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.put(x0 + i, y0 + y, col); } });
}
function makePuff(r, light, dark) {                   // a soft blob of smoke or steam in a few dithered levels, lit from below
  const s = r * 2 + 1, [c, x] = makeCanvas(s, s), img = x.createImageData(s, s), d = img.data, L = hex(light), D = hex(dark);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dd = Math.hypot(i - r, (y - r) * 1.1) / (r + 0.5);
    if (dd >= 1) continue;
    const q = Math.floor(Math.pow(1 - dd, 0.9) * 4 + bayer(i, y)) / 4;
    if (q <= 0) continue;
    const col = y - r > -r * 0.2 + (bayer(i + 1, y) - 0.5) * r * 0.6 ? L : D, o = (y * s + i) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = Math.round(Math.min(1, q) * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}
const tintCache = {};
function tint(col, tcol, a) {                         // #rrggbb between two colours
  const k = col + tcol + a;
  if (!tintCache[k]) {
    const A = hex(col), B = hex(tcol);
    tintCache[k] = '#' + [0, 1, 2].map(j => Math.round(A[j] + (B[j] - A[j]) * a).toString(16).padStart(2, '0')).join('');
  }
  return tintCache[k];
}
const darker = (c, a = 0.38) => tint(c, '#07040c', a);
const pick = (ramp, v, i, y) => ramp[clamp(Math.floor(v * (ramp.length - 1) + bayer(i, y)), 0, ramp.length - 1)];
function vnoise(x, y, s) {                            // smooth value noise in [0, 1)
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi, u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(hash(xi, yi, s), hash(xi + 1, yi, s), u), lerp(hash(xi, yi + 1, s), hash(xi + 1, yi + 1, s), u), v);
}
function drawScroll(cv, x0, y, k, t) {                // a W-wide tile drifting k widths a loop to the right
  const off = Math.floor(frac(k * t / LOOP) * W);
  ctx.drawImage(cv, x0 + off - W, y); ctx.drawImage(cv, x0 + off, y);
}

/* ================= Sky: deep blue, warmed over the square by its lamps and its smoke ================= */
const SKY = ['#02041a', '#030720', '#050a28', '#070e30', '#0a1338', '#0d1841', '#111d4a', '#162252', '#1d2756', '#262d5a',
  '#32335c', '#3e385c', '#4c3e5a', '#5a4456'];
function renderSky() {
  return ditherFill(W, H, SKY, (i, y) => {
    let v = Math.pow(Math.min(1, y / 168), 1.3) * 0.74;
    v += Math.max(0, 1 - Math.hypot((i - 220) / 300, (y - 176) / 92)) * 0.3;     // the square's glow in the smoke
    v += Math.max(0, 1 - Math.hypot((i - 232) / 70, (y - 110) / 90)) * 0.08;     // the floodlit tower in the haze
    return v;
  });
}
const STARS = [];
{
  const r = rng(1107);
  while (STARS.length < 120) {
    const x = Math.floor(r() * W), y = 2 + Math.floor(Math.pow(r(), 1.3) * 124);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 12 || (x > 214 && x < 250 && y > 8)) continue;
    const c = r();
    STARS.push({ x, y, k: 9 + Math.floor(r() * 60), ph: r(), b: (0.3 + r() * 0.6) * (1 - y / 170), big: r() < 0.06,
      col: c < 0.16 ? '#ffe2c0' : c < 0.32 ? '#c8d6ff' : '#eef0ff' });
  }
}
// a young crescent low in the west, lit from the sun under the horizon (bottom right); the rest by earthshine
function renderMoon() {
  const R = MOON.r, s = 2 * R + 1, p = painter(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const d = Math.hypot(i - R, y - R);
    if (d > R + 0.3) continue;
    if (Math.hypot(i - R + 2.4, y - R + 2.1) < R - 0.1) { p.put(i, y, d > R - 0.8 ? '#1c2350' : '#141a40'); continue; }
    p.put(i, y, d > R - 0.9 ? '#e8dcb2' : (i + y) % 5 === 0 ? '#f2e8c6' : '#fff6dc');
  }
  return p.done();
}
// the high haze: grill smoke and dust from the square, a thin tileable veil drifting slowly east to west
function tnoise(x, y, period, s) {                    // value noise whose lattice wraps every `period` cells in x
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi, u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  const a = ((xi % period) + period) % period, b = (a + 1) % period;
  return lerp(lerp(hash(a, yi, s), hash(b, yi, s), u), lerp(hash(a, yi + 1, s), hash(b, yi + 1, s), u), v);
}
function renderHaze(h, seed, cols, dens) {
  const p = painter(W, h);
  for (let y = 0; y < h; y++) for (let i = 0; i < W; i++) {
    const e = Math.sin(Math.PI * y / h);
    const d = 0.6 * tnoise(i / 40, y / 7 + seed, 12, seed) + 0.4 * tnoise(i / 16, y / 4, 30, seed + 1);
    const v = (d - 0.42) * 2.4 * e * dens;
    if (v > bayer(i, y) * 0.7 + 0.08) p.put(i, y, v > 0.55 + bayer(i + 2, y) * 0.3 ? cols[1] : cols[0]);
  }
  return p.done();
}

/* ================= The Koutoubia: a light map painted in the floodlights' colours =================
   Per pixel the stone's brightness (0..1, its relief times the floodlights' fall-off) and its kind: 0 stone, 1 the
   ceramic band, 2 a dark window, 3 a lantern window (lit per frame), 4 copper (the spire and its globes). Two palettes,
   gold and rose, are cross-faded per frame: the floodlights warm and cool very slowly. */
const STONE = {
  gold: ['#1e0e0c', '#3a1c12', '#5c2e18', '#82441e', '#a65e28', '#c47a34', '#dc9844', '#eeb85e', '#fad482', '#fff0be'],
  rose: ['#200c10', '#3c1816', '#5e281e', '#843a28', '#a84e32', '#c46640', '#dc8452', '#eea66c', '#f8c690', '#ffe4c4'],
};
const TILE_C = ['#0a2a2c', '#145652', '#228a7c', '#5ac2ac', '#e0e0c8'];
const COPPER = ['#2a120a', '#56260e', '#8c461a', '#c0702c', '#e8a250', '#fff0b4'];
const MIN_BOX = { x: 208, y: 8, w: 42, h: KT.base - 8 + 1 };
const LANTERN_WIN = [];                              // the lantern's window pixels, lit per frame
function buildMinaret() {
  const { x: X0, y: Y0, w, h } = MIN_BOX, L = new Float32Array(w * h).fill(-1), K = new Uint8Array(w * h);
  const S = (x, y, v, k = 0) => { x -= X0; y -= Y0; if (x < 0 || y < 0 || x >= w || y >= h) return; L[y * w + x] = clamp(v, 0, 1); K[y * w + x] = k; };
  const cx = (KT.x0 + KT.x1) / 2;
  // the floodlights stand round the foot: brightest low down and mid-face, the corners and the top a little dimmer
  const flood = (x, y, side) => {
    const v = 0.56 + 0.3 * smooth(58, KT.base, y) - 0.12 * Math.abs(x - cx) / 10 + 0.05 * Math.sin(y * 0.09);
    return side ? v * 0.5 + 0.02 * (KT.sx1 - x) : v;
  };
  const tex = (x, y) => (y % 3 === 0 ? -0.05 : 0) + (hash(x, y, 71) - 0.5) * 0.07 + (hash(x >> 1, y >> 2, 72) < 0.06 ? -0.07 : 0);
  // the shaft
  for (let y = KT.wall; y <= KT.base; y++) for (let x = KT.x0; x <= KT.sx1; x++) {
    const side = x > KT.x1, f = flood(x, y, side), lx = x - KT.x0;
    let v = f + tex(x, y), k = 0;
    if (y <= 67) v += 0.16;                                              // the cornice under the merlons
    else if (y === 68 || y === 72) v -= 0.22;
    else if (y >= 69 && y <= 71) { k = 1; v = y === 70 ? ((x & 1) ? 0.75 : 1) : (x % 3 === 0 ? 0.62 : 0.4); v *= side ? 0.55 : 1; }
    else if (y >= 73 && y <= 84) {                                       // a blind arcade of little lobed arches
      const m = side ? (x - KT.x1 - 1) % 3 : lx % 4, open = side ? m === 1 : m > 0;
      const arch = side ? y > 74 : (y > 74 || (y === 74 && m !== 1 && m !== 3) || (y === 73 && m === 2));
      if (open && arch) v -= 0.22; else if (!open) v += 0.08;
    } else if (y === 85 || y === 86) v += 0.12;
    if (!side && k === 0) {
      const ax = Math.abs(x - 230);
      if (y >= 88 && y <= 128 && ax <= 7) {                              // the great panel: a frame, a lobed arch, sebka lattice
        if (ax === 7 || y === 88 || y === 128) v += 0.14;
        else {
          const top = 89 + Math.round(7 - 7 * Math.sqrt(Math.max(0, 1 - (ax / 6.6) ** 2))) + (ax & 1 && ax > 2 ? 1 : 0);
          if (y >= top) {
            const a = ((x - 230 + y - 88) % 6 + 6) % 6, b = ((x - 230 - y + 88) % 6 + 6) % 6;
            v += a === 0 || b === 0 ? 0.08 : -0.17;
            if (y === top) v += 0.18;                                    // the lobes' lit edge
          }
        }
        if (y >= 100 && y <= 108 && (ax === 2 || ax === 3) && !(y === 100 && ax === 3)) { k = 2; v = y === 100 ? 0.5 : 0.25; }
        if (y >= 100 && y <= 108 && ax === 0) v += 0.12;                 // the colonnette between the twin windows
      }
      if (y >= 132 && y <= 145 && ax <= 4) {                             // a small lower window in its alfiz, plain stone below
        if (ax === 4 || y === 132 || y === 145) v += 0.14;
        else if (ax <= 1 && y >= 135 + ax && y <= 142) { k = 2; v = y === 135 + ax ? 0.45 : 0.22; }
      }
    }
    if (side && k === 0 && (x === 242) && ((y >= 96 && y <= 101) || (y >= 118 && y <= 123) || (y >= 140 && y <= 146))) { k = 2; v = 0.18; }
    if (x === KT.x1) v += 0.08;                                          // the corner catches the light
    S(x, y, v, k);
  }
  // stepped merlons along the parapet
  for (let x = KT.x0; x <= KT.sx1; x++) {
    const side = x > KT.x1, m = side ? (x - KT.x1 - 1) % 3 : (x - KT.x0) % 4;
    if (side ? m === 2 : m === 3) continue;
    const peak = side ? m === 0 : m === 1;
    for (let y = peak ? 60 : 63; y < KT.wall; y++) S(x, y, flood(x, 90, side) + (y === (peak ? 60 : 63) ? 0.2 : 0.06), 0);
  }
  // the lantern on the terrace, its merlons, the ribbed dome
  for (let y = LT.wall; y <= LT.base; y++) for (let x = LT.x0; x <= LT.sx1; x++) {
    const side = x > LT.x1;
    let v = (0.6 + 0.12 * (y - LT.wall) / 20) * (side ? 0.55 : 1) + tex(x, y), k = 0;
    if (y <= 43 || y >= 59) v += 0.14;
    else if (y === 44 || y === 47) v -= 0.18;
    else if (y === 45 || y === 46) { k = 1; v = (x + y) & 1 ? 0.85 : 0.5; v *= side ? 0.55 : 1; }
    if (y >= 49 && y <= 56 && (x === 228 || x === 229 || x === 231 || x === 232 || x === 236) && !(y === 49 && (x === 228 || x === 232))) {
      k = 3; v = 0.2; LANTERN_WIN.push([x, y, side]);
    }
    S(x, y, v, k);
  }
  for (let x = LT.x0; x <= LT.sx1; x++) {
    const m = (x - LT.x0) % 3;
    if (m === 2) continue;
    for (let y = m === 0 ? 37 : 39; y < LT.wall; y++) S(x, y, (x > LT.x1 ? 0.4 : 0.78) + (y === 37 || y === 39 ? 0.12 : 0));
  }
  [[31, 0], [32, 1], [33, 2], [34, 3], [35, 3], [36, 4]].forEach(([y, hw]) => {
    for (let x = 230 - hw; x <= 230 + hw; x++) S(x, y, 0.55 + ((x - 230) & 1 ? -0.1 : 0.06) - (x - 230) * 0.04 + (y === 31 ? 0.2 : 0));
  });
  // the spire: a copper pole and three gilded globes, biggest at the bottom, a finial on top
  for (let y = SP.tip + 1; y <= 30; y++) S(SP.x, y, 0.42, 4);
  S(SP.x, SP.tip, 0.8, 4);
  for (const [gy, r] of GLOBES) for (let y = Math.floor(gy - r); y <= Math.ceil(gy + r); y++) for (let x = Math.floor(SP.x - r); x <= Math.ceil(SP.x + r); x++) {
    const d = Math.hypot(x - SP.x, y - gy);
    if (d > r + 0.2) continue;
    S(x, y, 0.5 + 0.32 * (y - gy) / r - 0.18 * (x - SP.x) / r + (Math.hypot(x - SP.x + 0.8, y - gy + 0.9) < 0.8 ? 0.45 : 0), 4);
  }
  return { L, K, w, h };
}
function paintMinaret(m, name) {
  const p = painter(m.w, m.h), ramp = STONE[name];
  for (let y = 0; y < m.h; y++) for (let x = 0; x < m.w; x++) {
    const i = y * m.w + x, v = m.L[i];
    if (v < 0) continue;
    const X = x + MIN_BOX.x, Y = y + MIN_BOX.y, k = m.K[i];
    p.put(x, y, k === 0 ? pick(ramp, v, X, Y) : k === 1 ? pick(TILE_C, v, X, Y) : k === 4 ? pick(COPPER, v, X, Y) : k === 2 ? (v > 0.4 ? ramp[2] : '#160a0c') : '#2a120e');
  }
  return p.done();
}

/* ================= Far city, the Atlas, the prayer hall and the palms of the Koutoubia gardens ================= */
const FAR_WIN = [];                                  // far windows that come and go: { x, y, n, ph }
function renderFarCity() {
  const p = painter(W, H), r = rng(311);
  for (let x = 0; x < W; x++) {                      // the Atlas, faint beyond the city
    const ridge = Math.round(132 + 8 * vnoise(x / 46, 0, 3) + 3 * vnoise(x / 13, 1, 4));
    for (let y = ridge; y < 172; y++) p.put(x, y, y === ridge ? '#22244a' : pick(['#16173a', '#121433', '#0f102c'], (y - ridge) / 14, x, y));
  }
  for (let x = 0; x < W;) {                          // flat roofs of the medina, warmed from below by the square
    const w = 6 + Math.floor(r() * 12), roof = 146 + Math.floor(r() * 9);
    for (let i = x; i < x + w && i < W; i++) for (let y = roof; y < 172; y++) {
      const v = 0.18 + 0.5 * smooth(roof, 172, y) + (i === x ? -0.12 : 0) + (y === roof ? 0.16 : 0);
      p.put(i, y, pick(['#140c1a', '#1e1220', '#2a1824', '#3a2028', '#4c2a2c', '#5e3430'], v, i, y));
    }
    if (r() < 0.5) for (let i = x + 1; i < x + w - 1; i += 1 + Math.floor(r() * 3)) if (r() < 0.25) p.put(i, roof - 1, '#1a1222');   // parapet bits, a dish
    for (let k = 0; k < 2; k++) if (r() < 0.55) {
      const wx = x + 2 + Math.floor(r() * Math.max(1, w - 4)), wy = roof + 3 + Math.floor(r() * 8);
      FAR_WIN.push({ x: wx, y: wy, n: 3 + Math.floor(r() * 6), ph: r(), col: r() < 0.7 ? '#e8a860' : '#c8d8a0' });
    }
    x += w;
  }
  return p.done();
}
// the prayer hall's sawtooth of green-tiled gables over a pink wall with horseshoe niches, floodlit from the garden
function renderHall(p) {
  for (let x = 140; x < 262; x++) {
    const g = (x - 140) % 8, gy = 149 + Math.abs(g - 4) - (g === 4 ? 0 : 0);
    for (let y = gy; y < 154; y++) p.put(x, y, y === gy ? (g < 4 ? '#5aa47a' : '#3a7a58') : pick(['#0e2a20', '#164030', '#1e5a40'], 0.4 + (g < 4 ? 0.3 : 0) - (y - gy) * 0.08 + ((x + y) & 1 ? 0.05 : 0), x, y));
    for (let y = 154; y < 170; y++) {
      const niche = (x - 143) % 9, inN = niche >= 2 && niche <= 5 && y >= 158 + (niche === 2 || niche === 5 ? 1 : 0) && y <= 166;
      const lit = 0.3 + 0.45 * Math.max(0, 1 - Math.abs(x - 230) / 90) + (y === 154 ? 0.2 : 0);
      p.put(x, y, inN ? '#2a1214' : pick(STONE.rose.slice(1, 7), lit + (hash(x, y, 9) - 0.5) * 0.08, x, y));
    }
  }
}
const PALMS = [
  { x: 118, top: 108, base: 150, lean: 3, r: 12, seed: 7 },
  { x: 150, top: 92, base: 168, lean: -4, r: 15, seed: 1 },
  { x: 186, top: 110, base: 168, lean: 3, r: 13, seed: 2 },
  { x: 206, top: 132, base: 168, lean: -2, r: 10, seed: 3 },
  { x: 256, top: 128, base: 168, lean: 3, r: 11, seed: 4 },
  { x: 352, top: 104, base: 150, lean: 4, r: 14, seed: 5 },
  { x: 410, top: 116, base: 150, lean: -3, r: 13, seed: 6 },
];
function renderTrunk(p, P) {
  for (let y = P.top + 3; y <= P.base; y++) {
    const s = (P.base - y) / (P.base - P.top), x = Math.round(P.x + P.lean * Math.pow(s, 1.5));
    const ring = (y & 1) === 0;
    p.put(x, y, ring ? '#140c0c' : '#24160f'); p.put(x + 1, y, ring ? '#3a2416' : '#5a3a22');
    if (y > P.base - 12) p.put(x - 1, y, '#120a0c');
  }
  const tx = P.x + P.lean;
  for (let k = 0; k < 6; k++) pline(p, tx, P.top + 3, tx + (k - 2.5) * 1.6, P.top + 9 + (k & 1) * 2, '#2a1c12');   // the dry skirt
}
function renderCrown(P) {
  const R = P.r, cy = Math.round(R * 0.35) + 3, w = 2 * R + 7, h = cy + Math.round(R * 1.15) + 4, cx = R + 3, p = painter(w, h), r = rng(P.seed * 31 + 5);
  const n = 12;
  for (let k = 0; k < n; k++) {
    const a = Math.PI * (-0.12 + 1.24 * k / (n - 1)) + (r() - 0.5) * 0.18, len = R * (0.75 + r() * 0.3), droop = 0.9 + r() * 0.5;
    const steps = Math.ceil(len * 1.6);
    for (let j = 0; j <= steps; j++) {
      const s = j / steps, x = cx + Math.cos(a) * s * len, y = cy - Math.sin(a) * s * len * 0.75 + droop * s * s * len * 0.75;
      p.put(x, y, s > 0.8 ? '#3a4628' : '#1a2a1a');
      if (s > 0.15 && j % 2 === 0) {                                     // leaflets hanging off the spine, lit from below at the tips
        p.put(x, y + 1, s > 0.6 ? '#52502c' : '#0e1a12');
        if (s > 0.35 && s < 0.9) p.put(x + (Math.cos(a) > 0 ? -1 : 1), y + 2, '#0c1610');
      }
    }
  }
  for (let y = cy - 1; y <= cy + 2; y++) for (let x = cx - 1; x <= cx + 1; x++) p.put(x, y, '#120e0a');
  for (const [dx, dy] of [[-2, 3], [-1, 4], [1, 3], [2, 4], [0, 5]]) p.put(cx + dx, cy + dy, (dx + dy) & 1 ? '#c86a1e' : '#8a4414');   // dates
  return { c: p.done(), cx, cy };
}

/* ================= The houses round the square: pink-ochre walls, café terraces, the far row of stalls ================= */
const OCHRE = ['#1a0e14', '#28141a', '#3a1c1e', '#4e2622', '#663226', '#7e402c', '#985034', '#b2623e', '#c8784a'];
const WIN = [];                                      // windows that switch on and off: { x, y, w, h, n, ph, col }
const STRANDS = [];                                  // string-light bulbs: { x, y, k, c }
const TERRACES = [                                   // [x0, x1, parapet row, awning row, stripe colours, poles]
  { x0: 0, x1: 122, par: 134, awn: 110, cols: ['#b8462e', '#e8d8b8'], poles: [3, 52, 101] },
  { x0: 334, x1: 420, par: 140, awn: 117, cols: ['#2e6a4a', '#e8dcc0'], poles: [336, 378, 418] },
  { x0: 420, x1: 480, par: 126, awn: 104, cols: ['#c89a32', '#3a2a5a'], poles: [424, 462] },
];
function strand(ax, ay, bx, by, sag, every, colour) {   // a sagging string of bulbs from a to b
  const n = Math.max(1, Math.round(Math.abs(bx - ax) / every));
  for (let k = 0; k <= n; k++) { const f = k / n; STRANDS.push({ x: Math.round(lerp(ax, bx, f)), y: Math.round(lerp(ay, by, f) + sag * Math.sin(Math.PI * f)), k: STRANDS.length, c: colour }); }
}
const wallLight = (x, y, top) => clamp(0.18 + 0.5 * smooth(top, 178, y) + 0.2 * Math.max(0, 1 - Math.hypot((x - 60) / 70, (y - 176) / 24))
  + 0.2 * Math.max(0, 1 - Math.hypot((x - 360) / 60, (y - 176) / 22)), 0, 1);
function facade(p, x0, x1, top, r, rows) {
  for (let y = top; y < 179; y++) for (let x = x0; x < x1; x++) {
    let v = wallLight(x, y, top) + (hash(x >> 2, y >> 1, 5) < 0.1 ? -0.08 : 0) + (hash(x, y, 6) - 0.5) * 0.05;
    if (y === top || y === top + 1) v += 0.1;
    p.put(x, y, pick(OCHRE, v, x, y));
  }
  for (const wy of rows) for (let wx = x0 + 6; wx + 5 < x1; wx += 14 + Math.floor(r() * 6)) {
    const kind = r();
    for (let y = wy; y < wy + 8; y++) for (let x = wx; x < wx + 5; x++) if (!(y === wy && (x === wx || x === wx + 4))) p.put(x, y, '#120a10');
    for (let x = wx - 1; x <= wx + 5; x++) p.put(x, wy + 8, pick(OCHRE, wallLight(x, wy + 8, top) + 0.25, x, wy + 8));   // the sill
    if (kind < 0.3) for (let y = wy + 1; y < wy + 8; y++) for (let x = wx; x < wx + 5; x++) p.put(x, y, (x + y) & 1 ? '#3a2216' : '#5a3620');   // carved shutters
    else if (kind < 0.85) WIN.push({ x: wx, y: wy, w: 5, h: 8, n: 2 + Math.floor(r() * 5), ph: r(), col: r() < 0.25 ? '#e0d8a8' : r() < 0.6 ? '#ffc070' : '#ff9a50' });
  }
}
function terrace(p, T, r) {
  for (let x = T.x0; x < T.x1; x++) {                 // the parapet with its little crenels
    for (let y = T.par - 2; y < T.par + 1; y++) p.put(x, y, pick(OCHRE, 0.35 + (y === T.par - 2 ? 0.2 : 0), x, y));
    if ((x - T.x0) % 4 === 0) { p.put(x, T.par - 3, OCHRE[3]); p.put(x + 1, T.par - 3, OCHRE[3]); }
  }
  for (let y = T.awn + 6; y < T.par - 2; y++) for (let x = T.x0; x < T.x1; x++)   // under the awning: the warm room behind
    p.put(x, y, pick(['#3a1c18', '#5a2c20', '#7a3e28', '#9a5430'], 0.35 + 0.45 * Math.max(0, 1 - Math.abs(x - (T.x0 + T.x1) / 2) / 60) - (y - T.awn) * 0.012, x, y));
  for (const px0 of T.poles) for (let y = T.awn; y < T.par - 2; y++) { p.put(px0, y, '#1e1416'); p.put(px0 + 1, y, '#3a2a26'); }
  for (let x = T.x0; x < T.x1; x++) {                 // the striped awning, its scalloped valance
    const stripe = Math.floor((x - T.x0) / 3) & 1, base = T.cols[stripe];
    for (let y = T.awn; y < T.awn + 5; y++) p.put(x, y, tint(base, '#0a0612', 0.55 - (y - T.awn) * 0.07));
    const sc = (x - T.x0) % 6;
    p.put(x, T.awn + 5, tint(base, '#0a0612', 0.2));
    if (sc > 0 && sc < 5) p.put(x, T.awn + 6, tint(base, '#0a0612', 0.3));
    if (sc === 2 || sc === 3) p.put(x, T.awn + 7, tint(base, '#0a0612', 0.4));
  }
  strand(T.x0 - 2, T.awn + 8, T.x1 + 2, T.awn + 9, 3, 4, 0);
  strand(T.x0 - 2, T.par - 6, T.x1 + 2, T.par - 5, 2, 5, 1);
}
function renderHallLayer() {                         // the prayer hall and the palms' trunks (their crowns sway per frame)
  const p = painter(W, H);
  renderHall(p);
  for (const P of PALMS) renderTrunk(p, P);
  return p.done();
}
function renderTown() {
  const p = painter(W, H), r = rng(523);
  // the houses: left with its café terrace, right with the sign on its roof and a terrace beyond, a taller one at the edge
  facade(p, 0, 123, 134, r, [140, 154]);
  facade(p, 262, 421, 140, r, [146, 158]);
  facade(p, 420, W, 126, r, [132, 144]);
  for (const T of TERRACES) terrace(p, T, r);
  // the far row of stalls under the minaret: white tarps glowing from the lamps inside, smoke going up (per frame)
  for (const x0 of FAR_STALLS) {
    for (let y = 160; y < 166; y++) {
      const ins = 3 - Math.round((y - 160) * 0.6);
      for (let x = x0 + ins; x < x0 + 20 - ins; x++) p.put(x, y, pick(['#6a5a52', '#9a8670', '#c8b292', '#e8d6b0', '#fff0cc'], 0.3 + (y - 160) * 0.14 - Math.abs(x - x0 - 10) * 0.012, x, y));
    }
    for (let y = 166; y < 173; y++) for (let x = x0; x < x0 + 20; x++)
      p.put(x, y, x === x0 || x === x0 + 19 ? '#2a1a18' : pick(['#5a2a18', '#9a5428', '#d88a3a', '#f8c070'], 0.75 - (y - 166) * 0.07 - (hash(x, y, 12) < 0.25 ? 0.4 : 0), x, y));
    for (let x = x0; x < x0 + 20; x++) { p.put(x, 173, '#e8d4b0'); for (let y = 174; y < FAR; y++) p.put(x, y, pick(['#2a1a1a', '#4a3028', '#6a4632'], 0.6 - (y - 174) * 0.15, x, y)); }
  }
  for (let y = 166; y < 179; y++) for (let x = 307; x < 316; x++) {   // a riad's doorway, lit inside
    const arch = y > 167 || (x > 308 && x < 314);
    if (x === 307 || x === 315 || !arch) p.put(x, y, OCHRE[2]); else p.put(x, y, pick(['#c86a2a', '#ffb860', '#ffe0a0'], 0.8 - (y - 166) * 0.04, x, y));
  }
  for (let y = 139; y < 147; y++) for (let x = 82; x < 102; x++) p.put(x, y, y === 139 || y === 146 ? '#0e2a20' : '#174a36');   // CAFE and HOTEL
  for (let y = 145; y < 153; y++) for (let x = 360; x < 384; x++) p.put(x, y, y === 145 || y === 152 ? '#2a0e10' : '#5a1a1c');
  // the square: dusty paving, warmer and lighter where the lamps of the stalls and the cart fall
  for (let y = 172; y < H; y++) for (let x = 0; x < W; x++) {
    if (y < 179 && p.has(x, y)) continue;
    const pool = POOLS.reduce((m, [px0, py, rx, ry, a]) => Math.max(m, a * Math.max(0, 1 - Math.hypot((x - px0) / rx, (y - py) / ry))), 0);
    const slab = (y - 172) % Math.max(3, Math.round((y - 160) / 6)) === 0 ? -0.06 : 0;
    const v = 0.14 + 0.24 * (y - 172) / 98 + pool * 0.62 + slab + (hash(x >> 1, y, 21) - 0.5) * 0.08 + (vnoise(x / 16, y / 5, 22) - 0.5) * 0.12;
    p.put(x, y, pick(GROUNDC, v, x, y));
  }
  for (let k = 0; k < 160; k++) { const x = Math.floor(r() * W), y = 182 + Math.floor(r() * 86); p.put(x, y, r() < 0.5 ? '#14090c' : '#a07a5a'); }   // scraps, pebbles
  const c = p.done();
  text(p.x, 'CAFE', 84, 141, '#f0e4c4'); text(p.x, 'HOTEL', 362, 147, '#f8d8a0');
  return c;
}
const FAR_STALLS = [118, 142, 166, 190, 214, 238, 262, 286];
const GROUNDC = ['#100a12', '#180e16', '#22141a', '#2e1a1e', '#3c2224', '#4c2c28', '#5e382e', '#724434', '#88543c', '#a06646'];
// pools of light on the ground: [x, y, rx, ry, strength]
const POOLS = [[62, 204, 70, 14, 0.75], [360, 204, 62, 13, 0.7], [205, 180, 110, 8, 0.45], [32, 242, 40, 12, 0.7],
  [452, 244, 50, 18, 0.65], [240, 262, 200, 10, 0.18]];   // (the storyteller's and the gnawa's lamps lay theirs per frame)

/* ================= The rooftop sign: a steel frame on the right-hand house, two lamps over its face ================= */
const BB_FR = { x: BB.x - 4, y: BB.y - 9, w: BB.w + 8, h: BB_ROOF - BB.y + 10 };
const BB_LAMPS = [BB.x + 12, BB.x + BB.w - 13];
const STEEL = ['#7a7488', '#565066', '#3a3448', '#26202e', '#18141e'];
function renderBillboardFrame() {
  const p = painter(BB_FR.w, BB_FR.h), S = (x, y, col) => p.put(x - BB_FR.x, y - BB_FR.y, col);
  const x0 = BB.x - 1, x1 = BB.x + BB.w, y0 = BB.y - 1, y1 = BB.y + BB.h;
  for (let i = x0 - 1; i <= x1 + 1; i++) for (let y = y0 - 1; y <= y1 + 1; y++) {
    if (i < x0 || i > x1 || y < y0 || y > y1) S(i, y, STEEL[3]);
    else if (i === x0 || i === x1 || y === y0 || y === y1) S(i, y, i === x0 || y === y0 ? STEEL[1] : STEEL[2]);
  }
  const LEGS = [x0 + 4, BB.x + (BB.w >> 1) - 1, x1 - 5];
  for (const lx of LEGS) for (let y = y1 + 2; y < BB_ROOF - 2; y++) { S(lx, y, STEEL[2]); S(lx + 1, y, STEEL[3]); }
  for (let k = 0; k + 1 < LEGS.length; k++) {
    const a = LEGS[k] + 2, b = LEGS[k + 1] - 1;
    for (let i = a; i <= b; i++) { const f = (i - a) / (b - a); S(i, Math.round(lerp(y1 + 4, BB_ROOF - 3, f)), STEEL[3]); S(i, Math.round(lerp(BB_ROOF - 3, y1 + 4, f)), STEEL[3]); }
  }
  for (let i = x0 - 2; i <= x1 + 2; i++) S(i, y1 + 2, (i & 1) ? '#5a5268' : '#463e56');   // the catwalk
  for (const lx of BB_LAMPS) {
    for (let y = y0 - 5; y < y0 - 1; y++) S(lx, y, STEEL[2]);
    S(lx - 1, y0 - 8, STEEL[1]); S(lx, y0 - 8, STEEL[1]); S(lx + 1, y0 - 8, STEEL[2]);
    S(lx - 1, y0 - 7, STEEL[2]); S(lx, y0 - 7, STEEL[1]); S(lx + 1, y0 - 7, STEEL[3]);
    S(lx - 1, y0 - 6, '#e8d8b0'); S(lx, y0 - 6, '#fff8e0'); S(lx + 1, y0 - 6, '#d8c8a0');
  }
  return p.done();
}
// the city's own ad, an invented mint-tea house: a silver teapot held high pours a long stream into a glass with a
// sprig of mint; DAR NANA, then MINT TEA or ATAY 4 DH (tea, four dirhams) every 8 s; a zellige border, Moroccan green
const TEAPOT = [
  '....k.......',
  '...lll......',
  '..sSSSs.....',
  'h.SSWSSs....',
  'h.SSWSSSs..p',
  'hhSSSSSSpp.p',
  '..sSSSSs....',
  '...ssss.....',
  '..bbbbbb....',
];
const TEAPOT_KEY = { k: '#f0d070', l: '#a8acbc', s: '#6a7084', S: '#c4c8d8', W: '#ffffff', h: '#8a90a4', p: '#b4b8c8', b: '#585e70' };
const ZELLIGE = ['#2a9a86', '#f0e6d0', '#2a9a86', '#d8442e'];
function renderAd(line2) {
  const p = painter(BB.w, BB.h);
  for (let y = 0; y < BB.h; y++) for (let i = 0; i < BB.w; i++) p.put(i, y, pick(['#0a3226', '#0e4232', '#14543e', '#1a644a'], 0.85 - y / BB.h * 0.6 - Math.abs(i - 30) / 90, i, y));
  for (let i = 0; i < BB.w; i++) { p.put(i, 0, '#d8a848'); p.put(i, BB.h - 1, '#d8a848'); p.put(i, 1, ZELLIGE[i % 4]); p.put(i, BB.h - 2, ZELLIGE[(i + 2) % 4]); }
  stamp(p, TEAPOT, TEAPOT_KEY, 1, 2);
  for (let y = 7; y <= 12; y++) p.put(12, y, y & 1 ? '#e0a040' : '#c87a28');                 // the stream, poured from high up
  stamp(p, ['.mGm.', 'g...g', 'gaaag', 'gaaag', '.gag.', '.ggg.'], { m: '#7ae07a', G: '#3aa04a', g: '#e8d090', a: '#c8742a' }, 10, 12);   // the glass and its mint
  const c = p.done();
  text(p.x, 'DAR NANA', 16, 3, '#f8ecd0');
  text(p.x, line2, 16, 11, line2 === 'MINT TEA' ? '#8ae8a8' : '#f0c860');
  return c;
}

/* ================= The two near stalls: glowing tarps, bulbs, food on the counter, the bench in front ================= */
const BULBS = [];                                    // hanging bulbs under the stalls' eaves: { x, y, k }
const FOOD = [                                       // what the grill stall shows on its counter: [width, colours]
  [5, ['#b4562a', '#e08040', '#7a3018']], [4, ['#3a8a3a', '#c8d050', '#e04a3a']], [6, ['#8a4a20', '#5a2a10', '#c87030']],
  [4, ['#e8d8a8', '#c8a870', '#f4ecd0']], [5, ['#5a2a5a', '#8a4a8a', '#3a1a3a']], [4, ['#e0a040', '#f4d070', '#b07020']],
];
function renderStall(pin, pout, S, kind) {
  const { x0, x1 } = S, mid = (x0 + x1) >> 1;
  // inside: a warm back wall, shelves of plates and jars, a board
  for (let y = EAVE; y < COUNTER + 2; y++) for (let x = x0 + 1; x < x1; x++) {
    let col = pick(['#2a120e', '#4a2214', '#6e381e', '#8e4c26', '#ae6430'], 0.55 + 0.25 * Math.sin(Math.PI * (x - x0) / (x1 - x0)) - (y - EAVE) * 0.02, x, y);
    if (y === 180 || y === 184) col = '#2a1610';
    if ((y === 179 || y === 183) && (x * 7) % 5 < 2) col = (x & 1) ? '#e8e4d8' : '#c86a2a';   // plates, jars
    pin.put(x, y, col);
  }
  const bw = kind === 'snail' ? textWidth('BABBOUCHE') + 4 : textWidth('GRILL') + 4, bx = x0 + 6;
  pin.box(bx, 176, bw, 8, '#14100e'); pin.box(bx, 176, bw, 1, '#5a4a3a');
  // the counter: food on top, white tiles in front with a green band, the grill or the snail pot
  for (let x = x0; x < x1; x++) for (let y = COUNTER; y < FRONT; y++) {
    let col;
    if (y < COUNTER + 2) col = y === COUNTER ? '#e8dcc4' : '#a89a84';
    else if (y === COUNTER + 2 || y === FRONT - 2) col = '#1e6a4a';
    else col = ((x >> 1) + (y >> 1)) & 1 ? '#d8d4c6' : '#bab4a6';
    if (y === FRONT - 1) col = '#2a2024';
    pout.put(x, y, col);
  }
  if (kind === 'grill') {
    for (let x = x0 + 3, i = 0; x < x1 - 22; i++) {
      const [w, cols] = FOOD[i % FOOD.length];
      for (let k = 0; k < w; k++) { pout.put(x + k, COUNTER - 1, cols[(k + i) % 3]); if (k > 0 && k < w - 1) pout.put(x + k, COUNTER - 2, cols[(k * 2 + i) % 3]); }
      if (i % 3 === 0) { pout.put(x + 2, COUNTER - 3, cols[0]); pout.put(x + 1, COUNTER - 2, cols[0]); pout.put(x + 3, COUNTER - 2, cols[2]); }   // a tagine's cone
      x += w + 1;
    }
    for (let x = x1 - 20; x < x1 - 2; x++) for (let y = COUNTER - 2; y < COUNTER + 1; y++) pout.put(x, y, y === COUNTER - 2 ? '#3a3438' : '#18141a');   // the grill box
  } else {
    for (let y = 184; y < COUNTER; y++) {             // the big pot of snails in broth, a brass rim
      const hw = y < 186 ? 6 : 7;
      for (let x = POT - hw; x <= POT + hw; x++) pout.put(x, y, y === 184 ? '#d8a848' : y === 185 ? '#2a1a12' : pick(['#3a3a44', '#5a5a66', '#7a7a88'], 0.5 + (POT - x) / 18 - (y - 186) * 0.04, x, y));
    }
    for (let x = x0 + 4; x < x1 - 4; x += 4) if (Math.abs(x - POT) > 9) {
      pout.put(x, COUNTER - 1, '#f0ece0'); pout.put(x + 1, COUNTER - 1, '#f0ece0'); pout.put(x + 2, COUNTER - 1, '#d8d4c8'); pout.put(x + 1, COUNTER - 2, '#4a3a2a');
    }
  }
  // the bench in front, where the diners sit
  for (let x = x0 + 2; x < x1 - 2; x++) { pout.put(x, BENCH - 4, '#6a4a2e'); pout.put(x, BENCH - 3, '#3a2618'); if ((x - x0) % 12 === 4) { pout.put(x, BENCH - 2, '#2a1a12'); pout.put(x, BENCH - 1, '#2a1a12'); pout.put(x, BENCH, '#1a1010'); } }
  // poles and the tarp: a sloping roof glowing from the lamps inside, a valance with the stall's number
  for (const px0 of [x0, mid, x1 - 1]) for (let y = 166; y < FRONT; y++) if (y < COUNTER || px0 !== mid) { pout.put(px0, y, '#2a2430'); }
  for (let y = 162; y < EAVE; y++) {
    const roof = y < 168, ins = roof ? Math.round((168 - y) * 0.9) : -1;
    for (let x = x0 + ins; x < x1 - ins; x++) {
      const v = roof ? 0.35 + (y - 162) * 0.09 + (hash(x >> 3, 3, 4) - 0.5) * 0.12 : 0.85 - (y - 168) * 0.05;
      pout.put(x, y, pick(['#6a5a50', '#9a8672', '#c8b494', '#e8d8b4', '#fff2d0'], v - ((x - x0) % 16 === 0 ? 0.25 : 0), x, y));
    }
  }
  for (let x = x0 - 1; x <= x1; x++) pout.put(x, EAVE - 1, kind === 'grill' ? '#b8402a' : '#2a6aa8');   // a coloured stripe at the hem
  pout.box(mid - 7, 168, 14, 7, '#0e4a30'); pout.box(mid - 7, 168, 14, 1, '#2a8a5a');
  for (let x = x0 + 6; x < x1 - 4; x += 10) BULBS.push({ x, y: EAVE + 1, k: BULBS.length });
  return { bx, label: kind === 'snail' ? 'BABBOUCHE' : 'GRILL', mid };
}

/* ================= The orange-juice cart (48 x 44, its wheels on JUICE.y) ================= */
const ORANGE = ['#b85a10', '#f08a1a', '#ffc060'];
function renderJuiceCart() {
  const p = painter(48, 44);
  for (const x of [1, 45]) for (let y = 9; y < 30; y++) { p.put(x, y, '#16402a'); p.put(x + 1, y, '#0c2618'); }
  for (let x = 0; x < 48; x++) { p.put(x, 9, '#1e5a3a'); p.put(x, 10, '#0e301e'); }
  const pyramid = (cx) => {
    for (let row = 0; row < 5; row++) {
      const n = 7 - row, y = 25 - row * 2;
      for (let k = 0; k < n; k++) {
        const x = cx - n + k * 2 + 1;
        p.put(x, y, ORANGE[1]); p.put(x + 1, y, ORANGE[0]); p.put(x, y - 1, ORANGE[2]); p.put(x + 1, y - 1, ORANGE[1]);
      }
    }
  };
  pyramid(10); pyramid(37);
  for (let y = 17; y < 27; y++) { p.put(23, y, '#c8ccd8'); p.put(24, y, '#8a90a0'); }      // the press
  p.box(21, 17, 6, 2, '#d8dce8'); p.box(20, 22, 8, 1, '#a8acb8');
  for (const x of [19, 28]) { p.put(x, 25, '#f0e8d8'); p.put(x, 26, '#f09a2a'); }            // two glasses ready
  for (let x = 0; x < 48; x++) {
    p.put(x, 27, '#ece4d4');
    for (let y = 28; y < 38; y++) p.put(x, y, x === 0 || x === 47 || y === 28 || y === 37 ? '#e0dccc' : pick(['#0e3a22', '#165232', '#1e6a3e'], 0.5 + 0.3 * Math.sin(Math.PI * x / 48), x, y));
    p.put(x, 38, '#14101a');
  }
  for (const cx of [8, 39]) for (let y = 36; y < 44; y++) for (let x = cx - 4; x <= cx + 4; x++) {
    const d = Math.hypot(x - cx, y - 40);
    if (d < 3.6) p.put(x, y, d > 2.6 ? '#1a1418' : (x === cx || y === 40) ? '#8a8494' : '#2a2430');
  }
  const c = p.done();
  text(p.x, 'JUS 7', 14, 30, '#ffe070');
  return c;
}

/* ================= The lantern seller's stall: a kilim backdrop, two bars of hanging lanterns, a rug of them ================= */
const LANTS = [
  { x: 431, bar: LANT.bar, len: 5, shape: 0, col: '#ffcf70', k: 2, ph: 0.1 }, { x: 441, bar: LANT.bar, len: 12, shape: 1, col: '#ff9a50', k: -1, ph: 0.4 },
  { x: 452, bar: LANT.bar, len: 4, shape: 2, col: '#ff5a4a', k: 3, ph: 0.7 }, { x: 463, bar: LANT.bar, len: 10, shape: 0, col: '#ffe08a', k: -2, ph: 0.2 },
  { x: 473, bar: LANT.bar, len: 5, shape: 1, col: '#ffcf70', k: 1, ph: 0.9 }, { x: 436, bar: LANT.bar2, len: 8, shape: 2, col: '#5aa8ff', k: -3, ph: 0.5 },
  { x: 447, bar: LANT.bar2, len: 14, shape: 1, col: '#ffd890', k: 2, ph: 0.3 }, { x: 458, bar: LANT.bar2, len: 6, shape: 0, col: '#ffb860', k: -1, ph: 0.6 },
  { x: 469, bar: LANT.bar2, len: 12, shape: 2, col: '#6ae08a', k: 2, ph: 0.8 },
];
const LANT_SHAPES = [
  ['...c...', '..ccc..', '.ccccc.', 'mmmmmmm', 'mxmxmxm', 'mmxmxmm', 'mxmxmxm', 'mmxmxmm', 'mxmxmxm', 'mmmmmmm', '.mmmmm.', '..mmm..', '...m...'],
  ['...c...', '..mmm..', '.mxmxm.', 'mxmxmxm', 'xmxmxmx', 'mxmxmxm', '.mxmxm.', '..mmm..', '...m...'],
  ['..c..', '.ccc.', 'mxmxm', 'mxmxm', 'mxmxm', 'mxmxm', 'mmmmm', '.mmm.', '..m..'],
];
const turnRows = rows => rows.map(r => r.replace(/[mx]/g, ch => ch === 'm' ? 'x' : 'm').replace(/^x|x$/g, 'm'));   // a quarter turn on
function lanternSprites(L) {
  const glass = L.shape === 2;
  const key = { c: '#9a6a2a', m: glass ? '#4a3016' : '#6a4418', x: L.col };
  const rows = LANT_SHAPES[L.shape];
  return [sprite(rows, key), sprite(turnRows(rows), { ...key, x: glass ? tint(L.col, '#ffffff', 0.35) : L.col })];
}
const KILIM = ['#3a0c16', '#5a1420', '#7a2a1e', '#b8742a', '#1e2450'];
function renderLanternBack() {
  const p = painter(W, H);
  for (let y = 154; y < 237; y++) for (let x = 426; x < W; x++) {
    const band = Math.floor((y - 154) / 9), d = Math.abs(((x - 426) % 10) - 5) + Math.abs(((y - 154) % 9) - 4);
    let v = band % 3 === 1 ? (d < 3 ? 3 : d === 3 ? 4 : 1) : (y - 154) % 9 === 0 ? 2 : 1;
    if ((x + y) % 7 === 0 && v === 1) v = 0;
    p.put(x, y, tint(KILIM[v], '#05030a', 0.35 + 0.25 * (y - 154) / 82));
  }
  for (const x of [424, 478]) for (let y = 147; y < 252; y++) { p.put(x, y, '#2a1a10'); p.put(x + 1, y, '#4a2e18'); }
  for (const by of [LANT.bar, LANT.bar2]) for (let x = 418; x < W; x++) { p.put(x, by, '#5a3a20'); p.put(x, by + 1, '#2a1a10'); }
  return p.done();
}
const RUG_LANTS = [[430, 0], [440, 2], [466, 1], [474, 0]];   // lanterns standing on the rug: [x, shape]
function renderLanternRug() {
  const p = painter(W, H);
  for (let y = LANT.rug; y < 252; y++) {
    const x0 = 418 - Math.round((y - LANT.rug) * 0.6);
    for (let x = x0; x < W; x++) {
      const edge = y === LANT.rug || y === 251 || x === x0;
      p.put(x, y, edge ? '#c8a050' : pick(['#3a0e14', '#6a1a1e', '#8a2a20', '#2a2a5a'], ((x >> 2) + (y >> 1)) % 5 === 0 ? 0.95 : 0.4 + (hash(x >> 1, y, 3) - 0.5) * 0.2, x, y));
    }
  }
  for (const [x, shape] of RUG_LANTS) stamp(p, LANT_SHAPES[shape], { c: '#9a6a2a', m: '#7a5020', x: '#ffd890' }, x - 3, LANT.rug + 6 - LANT_SHAPES[shape].length + 4);
  for (const x of [452, 458]) { p.box(x - 2, 247, 6, 1, '#d8a848'); p.box(x - 1, 248, 4, 1, '#8a6020'); }   // brass trays
  return p.done();
}

/* ================= People ================= */
// One function draws everyone, from a few measurements that follow from the height, so the same person can stand at
// any depth. put(i, j, col): i from the centre column, j from the feet row (0), negative upwards.
// S: { h, coat, legs, skin, hair, robe (a djellaba or kaftan to the ankles), hood, hat: 'fez' | 'cap' | 'turban' |
//      'scarf' | 'gnawa' | 'chef' | 'sun', hatCol, longHair, bag, pack, beard, dk (into the night), rim (the lamps' light) }
// pose: view 0 side (facing right) / 1 back / 2 front; stride -1 | 0 | 1; bob; arm 0 swing / 1 held out / 2 one up /
// 3 both up / 4 out to the sides / 5 hands together / 6 hand to the mouth; sit (side, on a seat); squat (on the ground,
// cross-legged); seat (on a bench, from behind: the legs are hidden)
const dims = h => { const hh = h >= 22 ? 5 : h >= 15 ? 4 : 3, th = Math.round(h * 0.36); return { hh, hw: h >= 20 ? 4 : 3, tw: h >= 22 ? 6 : h >= 15 ? 5 : 4, th, lh: h - hh - th }; };
const SHOE = '#0e0a10';
function drawFigure(put, S, { view = 0, stride = 0, bob = 0, arm = 0, sit = false, squat = false, seat = false } = {}) {
  const { hh, hw, tw, th, lh } = dims(S.h), big = S.h >= 20, lw = tw >= 5 ? 2 : 1, x0 = -((tw - 1) >> 1);
  const coatD = darker(S.coat), legsD = darker(S.legs, 0.3), sh = Math.max(3, Math.round(lh * 0.55));
  const R = (i, j, w, h, col) => { for (let y = j; y < j + h; y++) for (let x = i; x < i + w; x++) put(x, y, col); };
  const tb = sit ? -(sh + 1) : squat ? -2 : seat ? -sh : -lh - bob, tt = tb - th + 1;
  const robe = S.robe && !squat && !seat;
  if (sit) {
    const thigh = robe ? S.coat : S.legs;
    R(x0 + tw - 2, -sh - 1, 4, 2, thigh); R(x0 + tw + 1, -sh + 1, lw, sh - 1, robe ? coatD : S.legs); R(x0 + tw + 1, 0, lw + 1, 1, SHOE);
  } else if (squat) R(x0 - 1, -1, tw + 2, 2, darker(S.robe ? S.coat : S.legs, 0.25));
  else if (!seat && view === 0) {
    const half = lh >> 1, ext = big && stride === 1 ? 1 : 0;
    for (const [bx, s, col] of [[lw === 2 ? -1 : 0, -stride, legsD], [1, stride, S.legs]]) {
      R(bx + s, -lh + 1 - bob, lw, lh - half + bob, col);
      R(bx + s + Math.sign(s) * ext, -half + 1, lw, half - 1, col);
      R(bx + s + Math.sign(s) * ext, 0, lw + 1, 1, SHOE);
    }
  } else if (!seat) {
    const xl = x0 + (tw >= 5 ? 0 : 1), xr = x0 + tw - lw - (tw >= 5 ? 0 : 1);
    for (const [bx, col] of [[xl, view === 1 ? S.legs : legsD], [xr, S.legs]]) { R(bx, -lh + 1 - bob, lw, lh - 1 + bob, col); R(bx, 0, lw, 1, SHOE); }
  }
  R(x0, tt, tw, th, S.coat);                                       // the body, shaded on the far edge
  if (view === 0) R(x0, tt, 1, th, coatD); else { R(x0, tt + 1, 1, th - 1, coatD); R(x0 + tw - 1, tt + 1, 1, th - 1, coatD); }
  if (robe && !sit) {                                              // a djellaba or a kaftan down to the ankles, the hem swinging
    const n = -1 - tb, sw = view === 0 ? stride : 0;
    R(x0, tb + 1, tw, n, S.coat); R(x0, tb + 1, 1, n, coatD);
    if (sw) R(sw > 0 ? x0 + tw : x0 - 1, -2, 1, 2, coatD);
    if (S.trim) R(x0 + (view === 0 ? tw - 1 : tw >> 1), tt + 1, 1, n + th - 1, S.trim);
  } else if (!sit) R(x0, tb, tw, 1, coatD);
  if (S.pack) { if (view === 0) R(x0 - 1 - (big ? 1 : 0), tt + 1, 1 + (big ? 1 : 0), th - 2, S.pack); else if (view === 1) R(x0 + 1, tt + 1, tw - 2, th - 2, S.pack); }
  if (S.bag) { if (view === 0) R(x0 - 1, tb - 2, 2 + (big ? 1 : 0), 3, S.bag); else R(x0 + tw - 1, tb - 2, 2, 3, S.bag); }
  const fx = x0 + tw;                                              // the column just in front of the chest
  if (arm === 0 && view === 0) { const ax = x0 + 1 + (stride === 1 ? 0 : stride === -1 ? tw - 3 : (tw >> 1) - 1); R(ax, tt + 1, 1, th - 2, coatD); put(ax, tb - (sit ? 1 : 0), S.skin); }
  else if (arm === 1) { R(fx - 1, tt + 2, 2 + (big ? 1 : 0), 1, coatD); put(fx + 1 + (big ? 1 : 0), tt + 2, S.skin); }
  else if (arm === 2) {
    if (view === 0) { put(fx - 1, tt, coatD); put(fx, tt - 1, coatD); put(fx, tt - 2, coatD); put(fx + 1, tt - 3, S.skin); }
    else { R(fx, tt - 2, 1, 4, coatD); put(fx, tt - 3, S.skin); }
  } else if (arm === 3) { R(x0 - 1, tt - 3, 1, 4, coatD); R(fx, tt - 3, 1, 4, coatD); put(x0 - 1, tt - 4, S.skin); put(fx, tt - 4, S.skin); }
  else if (arm === 4) { R(x0 - 2, tt + 1, 2, 1, coatD); R(fx, tt + 1, 2, 1, coatD); put(x0 - 3, tt + 1, S.skin); put(fx + 2, tt + 1, S.skin); }
  else if (arm === 5) { put(fx, tt + 2, coatD); put(fx + (view === 0 ? 1 : -1), tt + 1, S.skin); if (view !== 0) { put(x0 - 1, tt + 2, coatD); put(x0, tt + 1, S.skin); } }
  else if (arm === 6) {
    if (view === 0) { put(fx - 1, tt + 2, coatD); put(fx, tt + 1, coatD); put(fx, tt, S.skin); }
    else if (view === 2) { put(fx - 1, tt + 1, coatD); put(fx - 2, tt, S.skin); }
    else { put(fx, tt + 1, coatD); put(fx - 1, tt, coatD); }
  }
  // the head and what is on it
  const hb = tt - 1, ht = hb - hh + 1, hx0 = view === 0 ? x0 + tw - hw - (tw - hw >= 2 ? 1 : 0) : x0 + ((tw - hw) >> 1), fh = hh >= 4 ? hh - 2 : 2;
  const covered = S.hood || S.hat === 'scarf';
  R(hx0, ht, hw, hh, S.hood ? S.coat : S.hat === 'scarf' ? S.hatCol : S.hair);
  if (view === 0) { R(hx0 + hw - 2, hb - fh + 1, 2, fh, S.skin); if (S.beard) R(hx0 + hw - 2, hb - 1, 2, 2, S.beard); }
  else if (view === 2) { R(hx0 + (hw === 4 ? 1 : 0), hb - fh + 1, hw === 4 ? 2 : 3, fh, S.skin); if (S.beard) R(hx0 + (hw === 4 ? 1 : 0), hb, hw === 4 ? 2 : 3, 1, S.beard); }
  if (S.hood) put(view === 0 ? hx0 : hx0 + (hw >> 1), ht - 1, S.coat);                     // the djellaba's pointed hood
  if (S.hat === 'scarf') { R(hx0 - (view === 1 ? 0 : 0), hb + 1, hw, 1, S.hatCol); if (view === 1) R(hx0, hb + 1, hw, 2, S.hatCol); }
  if (S.longHair && !covered) { if (view === 0) R(hx0, hb + 1, 1 + (big ? 1 : 0), 3, S.hair); else if (view === 1) R(hx0, hb + 1, hw, 3, S.hair); }
  if (S.hat === 'fez') { R(hx0, ht - 1, hw, 2, '#a81a24'); put(hx0 + (view === 0 ? 0 : hw - 1), ht, '#14080a'); }
  else if (S.hat === 'cap') { R(hx0, ht, hw, hh >= 5 ? 2 : 1, S.hatCol); if (view === 0) put(hx0 + hw, ht + (hh >= 5 ? 1 : 0), S.hatCol); }
  else if (S.hat === 'turban') { R(hx0 - 1, ht, hw + 2, 2, '#f0ece0'); R(hx0, ht - 1, hw, 1, '#f8f4ec'); put(hx0 - 1, ht + 1, '#c8c4b8'); }
  else if (S.hat === 'chef') { R(hx0, ht - 2, hw, 3, '#f4f4f0'); put(hx0 + 1, ht - 2, '#ffffff'); }
  else if (S.hat === 'gnawa') { R(hx0, ht - 1, hw, 2, S.hatCol); put(hx0 + 1, ht, '#fff8e8'); put(hx0 + hw - 1, ht - 1, '#fff8e8'); }
  else if (S.hat === 'sun') { R(hx0 - 1, ht, hw + 2, 1, S.hatCol); R(hx0, ht - 1, hw, 1, S.hatCol); }
}
const SPR_W = 20, SPR_AX = 9;                                      // every figure sprite: 20 wide, the centre column at 9
const NIGHT = '#0c0814';
function figure(S, pose, flip = false) {
  const hgt = S.h + 10, p = painter(SPR_W, hgt), dk = S.dk ?? 0.2;
  drawFigure((i, j, col) => p.put(flip ? SPR_W - 1 - SPR_AX - i : SPR_AX + i, hgt - 1 + j, tint(col, NIGHT, dk)), S, pose);
  if (S.rim) for (let i = 0; i < SPR_W; i++) for (let y = 0; y < hgt; y++) if (p.has(i, y)) { p.put(i, y, tint(p.get(i, y), S.rimCol || '#ffc880', S.rim)); break; }
  const c = p.done(); c.flip = flip; c.person = true;
  return c;
}
// x: the figure's centre column, fy: its feet row
const blit = (spr, x, fy) => ctx.drawImage(spr, Math.round(x) - (spr.flip ? SPR_W - 1 - SPR_AX : SPR_AX), fy - spr.height + 1);
const WALK_POSES = [{ bob: 1 }, { stride: 1 }, { bob: 1 }, { stride: -1 }];
function walkSet(S, flip) { const a = figure(S, WALK_POSES[0], flip); return [a, figure(S, WALK_POSES[1], flip), a, figure(S, WALK_POSES[3], flip)]; }
function handAt(S) { const { tw, th, lh } = dims(S.h); return { x: -((tw - 1) >> 1) + tw + 1 + (S.h >= 20 ? 1 : 0), y: -lh - th + 3 }; }
function shadow(x, fy, w = 5) { ctx.globalAlpha = 0.3; ctx.fillStyle = '#07040a'; ctx.fillRect(Math.round(x) - (w >> 1), fy + 1, w, 1); ctx.globalAlpha = 1; }

const ROBES = ['#6a5a4a', '#8a7a62', '#4a4038', '#d8d0c0', '#3a3a48', '#5a3a2a', '#7a6a8a', '#2a3a4a', '#9a8a6a', '#b8b0a0', '#4a5a3a', '#e8e2d4'];
const KAFTANS = ['#b8304a', '#2a6a8a', '#d89a3a', '#6a2a6a', '#2a7a5a', '#e8e0d0', '#c85a3a', '#3a3a7a', '#e07aa0', '#1e2a4a'];
const SCARVES = ['#e84a6a', '#f0c040', '#e8e0d0', '#4a9ad0', '#2a2a3a', '#8a2a4a', '#5ab08a', '#c87a3a', '#f4f0e8'];
const TOPS = ['#2a3456', '#6a2a34', '#3a5a3a', '#d8c8a8', '#3a3a44', '#b84a3a', '#2e6a8a', '#e6e2da', '#c8a03a', '#5a3a6a', '#1e2230', '#d87a4a'];
const HAIRS = ['#141014', '#2a1a14', '#4a2e1a', '#8a5a2a', '#c8a060', '#9a9aa2', '#141014', '#2a1a14'];
const SKINS = ['#c88a64', '#b87a54', '#d8a07a', '#a8704a', '#e8b890', '#f0c8a0', '#8a5a3a'];
const LEGC = ['#16121c', '#20243a', '#2a2e3e', '#3a3a4a', '#4a4038', '#c8c0b0', '#1e2a2a', '#5a4a3a'];
const of = (r, list) => list[Math.floor(r() * list.length)];
function randomStyle(r, h) {
  const v = r(), S = { h, legs: of(r, LEGC), skin: of(r, SKINS.slice(0, 5)), hair: of(r, HAIRS) };
  if (v < 0.4) {                                                  // a man of the city in his djellaba
    Object.assign(S, { coat: of(r, ROBES), robe: true, skin: of(r, SKINS.slice(0, 4)) });
    const w = r();
    if (w < 0.3) S.hood = true; else if (w < 0.4) S.hat = 'fez'; else if (w < 0.5) { S.hat = 'cap'; S.hatCol = of(r, ['#e8e4d8', '#2a2a3a', '#8a2a2a']); }
    if (r() < 0.4) S.beard = r() < 0.3 ? '#9a9aa2' : '#1a1214';
    if (r() < 0.2) S.trim = '#e8d8a8';
  } else if (v < 0.7) {                                           // a woman in a kaftan or djellaba, a headscarf
    Object.assign(S, { coat: of(r, KAFTANS), robe: r() < 0.8, hat: r() < 0.82 ? 'scarf' : null, hatCol: of(r, SCARVES), skin: of(r, SKINS.slice(0, 5)) });
    if (!S.hat) S.longHair = true;
    if (r() < 0.3) S.bag = of(r, ['#c8a050', '#8a2a2a', '#2a2a3a']);
    if (r() < 0.25) S.trim = of(r, ['#f0c040', '#e8e0d0']);
  } else {                                                        // a visitor
    Object.assign(S, { coat: of(r, TOPS), skin: of(r, SKINS.slice(2, 6)) });
    const w = r();
    if (w < 0.18) { S.hat = 'cap'; S.hatCol = of(r, TOPS); } else if (w < 0.28) { S.hat = 'sun'; S.hatCol = '#e8d8a8'; }
    if (r() < 0.35) S.longHair = true;
    const b = r();
    if (b < 0.3) S.bag = of(r, ['#e8d8b0', '#d0343e', '#1e2230']); else if (b < 0.5) S.pack = of(r, ['#c8483a', '#2e4a7a', '#3a3a44', '#4a7a4a']);
  }
  return S;
}

/* ================= The calèche and the moped (facing right; flipped for the other way) ================= */
function makeCaleche(f) {
  const p = painter(66, 34), B = 33;
  const wheel = (cx, cy, r) => {
    for (let y = Math.floor(cy - r); y <= Math.ceil(cy + r); y++) for (let x = Math.floor(cx - r); x <= Math.ceil(cx + r); x++) {
      const d = Math.hypot(x - cx, y - cy);
      if (d > r + 0.3) continue;
      if (d > r - 1) p.put(x, y, '#1a1210');
      else { const a = Math.atan2(y - cy, x - cx) - f * Math.PI / 8, s = Math.abs(Math.sin(a * 3)); if (s < 0.3 || d < 1) p.put(x, y, '#a07a3a'); }
    }
  };
  for (let y = 7; y < 15; y++) {                                   // the folded black hood
    const hw = Math.round(8 * Math.sqrt(Math.max(0, 1 - ((15 - y) / 8.5) ** 2)));
    for (let x = 13 - hw; x <= 13 + hw; x++) p.put(x, y, x === 13 + hw ? '#3a3440' : (y - 7) % 3 === 0 ? '#2a2430' : '#141018');
  }
  p.box(8, 13, 14, 2, '#8a1e2a');                                  // the red seat
  for (let x = 4; x < 38; x++) for (let y = 15; y < 23; y++) p.put(x, y, y === 15 || y === 19 ? '#c8a040' : y === 22 ? '#0e1a14' : (x === 4 ? '#0e2a1c' : '#1e5034'));
  p.box(30, 11, 7, 2, '#3a2416'); p.box(35, 13, 1, 2, '#3a2416');
  // the driver, hood up, the reins in his hands
  p.box(31, 4, 3, 3, '#5a4632'); p.put(33, 5, '#b87a54'); p.put(33, 6, '#b87a54'); p.put(32, 3, '#5a4632');
  p.box(30, 7, 5, 5, '#6a5440'); p.box(30, 7, 1, 5, '#4a3a2a'); p.put(35, 9, '#4a3a2a'); p.put(36, 9, '#b87a54');
  // two visitors under the hood
  p.box(15, 8, 3, 3, '#c8a060'); p.box(15, 11, 3, 3, '#2e6a8a'); p.box(10, 8, 3, 3, '#2a1a14'); p.box(10, 11, 3, 3, '#e6e2da');
  const hx = 42;
  pline(p, 36, 20, hx + 8, 20, '#5a3a1e');                         // the shafts
  // the horse, a red blanket and a red pompom, his head down a little
  for (let y = 18; y < 25; y++) for (let x = hx + 2; x < hx + 16; x++) p.put(x, y, y === 18 ? '#9a6a3e' : y > 22 ? '#4a2c18' : '#7a4e2c');
  for (let y = 18; y < 21; y++) for (let x = hx + 5; x < hx + 12; x++) p.put(x, y, y === 20 ? '#d8b040' : '#9a2a2a');
  for (let k = 0; k < 5; k++) { p.box(hx + 14 + k, 17 - k, 2, 3, '#7a4e2c'); p.put(hx + 13 + k, 17 - k, '#1e1210'); }   // the neck and its mane
  p.box(hx + 18, 12, 4, 3, '#7a4e2c'); p.box(hx + 20, 14, 3, 2, '#5a3820'); p.put(hx + 19, 13, '#0a0608');
  p.put(hx + 18, 11, '#5a3a20'); p.put(hx + 19, 10, '#d83a3a'); p.put(hx + 20, 10, '#e84a3a');
  for (let y = 19; y < 26; y++) p.put(hx + 1 - (y > 22 ? 1 : 0), y, '#1e1210');                        // the tail
  const legs = [[hx + 3, 0], [hx + 5, 2], [hx + 13, 1], [hx + 15, 3]];
  for (const [lx, ph] of legs) {
    const q = (f + ph) & 3, lift = q === 0 ? 2 : q === 1 ? 1 : 0, sw = q < 2 ? 1 : 0;
    for (let y = 25; y < B - lift; y++) p.put(lx + (y > 28 ? sw : 0), y, ph & 1 ? '#3a2214' : '#4a2c18');
    p.put(lx + sw, B - lift, '#14100c');
  }
  wheel(13, 26, 7); wheel(32, 28, 5);
  return p.done();
}
function makeMoped(f) {
  const p = painter(22, 20), B = 19;
  const wheel = (cx) => { for (let y = B - 5; y <= B; y++) for (let x = cx - 3; x <= cx + 3; x++) { const d = Math.hypot(x - cx, y - (B - 2.5)); if (d < 3.1) p.put(x, y, d > 2.1 ? '#121014' : ((x + y + f) & 1) ? '#6a6a74' : '#2a2a30'); } };
  wheel(4); wheel(17);
  p.box(5, 12, 11, 3, '#a82a32'); p.box(6, 11, 6, 1, '#1a1418'); p.box(15, 7, 2, 7, '#8a8a94'); p.box(14, 6, 4, 1, '#3a3a40');
  p.put(18, 8, '#fff6d8'); p.put(18, 9, '#e8d8b0'); p.put(2, 12, '#ff3a2a');
  // the rider in a grey djellaba, hood down, a cap
  p.box(7, 4, 5, 7, '#6a6a6a'); p.box(7, 4, 1, 7, '#4a4a4a'); p.box(10, 11, 4, 2, '#5a5a5a'); p.box(13, 13, 1, 3, '#5a5a5a'); p.put(13, 16, SHOE);
  p.box(9, 0, 3, 4, '#1a1214'); p.box(11, 1, 1, 3, '#b87a54'); p.box(9, 0, 4, 1, '#2a3a6a');
  p.box(11, 6, 3, 1, '#5a5a5a'); p.put(14, 6, '#b87a54');
  return p.done();
}
function flipCanvas(src) {
  const [c, x] = makeCanvas(src.width, src.height);
  x.translate(src.width, 0); x.scale(-1, 1); x.drawImage(src, 0, 0);
  return c;
}

/* ================= The loop's timeline (loop seconds; see the table in the header) ================= */
const FLARE = 8;                                     // the grill cook fans hard for 2 s of every 8
const PUNCH = [58, 118, 178, 232];                   // the storyteller's punchlines: his ring laughs for 3 s
const MOPED = [{ a: 14, b: 22, dir: 1 }, { a: 106, b: 114, dir: -1 }, { a: 204, b: 212, dir: 1 }];
const CALECHE = [{ a: 38, b: 70, dir: -1 }, { a: 156, b: 188, dir: 1 }];
const PLANE = { a: 30, b: 62 };
const EGRETS = [{ a: 128, b: 146, dir: 1 }, { a: 208, b: 226, dir: -1 }];
const WATER = { a: 72, stop: 88, go: 104, b: 124, bells: [91, 93], x: 222, y: 221 };   // the water seller
const TOURIST = { a: 76, stop: 89, go: 103, b: 116, flash: 98, x: 233, y: 222 };          // and the visitor who photographs him
const GN = { a: 150, b: 200 };                       // the gnawa's build-up
const SALE = { a: 196, at: 204, lift: 206, give: 214, go: 218, b: 228 };                  // a couple buys a lantern
const CAT_RUN = { a: 2, b: 18 };
const LAUGH = t => PUNCH.some(p => t >= p && t < p + 3);
// the qraqeb's beat: two a second, quickening through the build-up (40 extra beats, even, so the loop joins)
const beatAt = t => Math.floor(t * 2 + 40 * smooth(GN.a, GN.b, t));
const walkX = (t, a, b, x0, x1) => Math.round(lerp(x0, x1, clamp((t - a) / (b - a), 0, 1)));

/* ================= The cast ================= */
const mk = (h, coat, extra = {}) => ({ h, coat, legs: '#2a2430', skin: '#b87a54', hair: '#1a1214', ...extra });
let CAST_SPR;
function buildCast() {
  const cook = (h, extra = {}) => mk(h, '#ece8e0', { robe: false, hat: 'chef', legs: '#3a3a44', rim: 0.35, dk: 0.05, ...extra });
  const story = mk(personH(STORY.y) + 1, '#ece4d4', { robe: true, hat: 'turban', beard: '#b8b8c0', skin: '#a8704a', rim: 0.4, rimCol: '#ffd890', dk: 0.08, trim: '#c8b890' });
  const gA = mk(personH(GNAWA.y), '#2a4a9a', { robe: true, hat: 'gnawa', hatCol: '#1a1a30', skin: '#6a4430', rim: 0.35, dk: 0.1, trim: '#f0c040' });
  const gB = mk(personH(GNAWA.y), '#b82a2a', { robe: true, hat: 'gnawa', hatCol: '#1a1a30', skin: '#7a4a30', rim: 0.35, dk: 0.1, trim: '#f0e0b0' });
  const gG = mk(personH(GNAWA.y), '#e8e4d8', { robe: true, hat: 'gnawa', hatCol: '#2a1a1a', skin: '#5a3a28', beard: '#141010', rim: 0.35, dk: 0.1 });
  const water = mk(personH(WATER.y), '#b81e24', { robe: true, skin: '#a8704a', beard: '#2a1a14', rim: 0.3, dk: 0.12, trim: '#f0c040' });
  const tourist = mk(personH(TOURIST.y), '#e6e2da', { legs: '#3a5a7a', hat: 'sun', hatCol: '#e8d8a8', skin: '#f0c8a0', hair: '#c8a060', pack: '#c8483a', rim: 0.3, dk: 0.12 });
  const seller = mk(personH(245), '#4a3a5a', { robe: true, hood: true, skin: '#9a6444', beard: '#3a2a20', rim: 0.45, rimCol: '#ffcf70', dk: 0.05 });
  const juice = cook(personH(233), { coat: '#f0ece4', hat: 'cap', hatCol: '#e04a2a' });
  const man = mk(personH(257), '#2e4a7a', { legs: '#c8c0b0', skin: '#f0c8a0', hair: '#4a2e1a', rim: 0.4, rimCol: '#ffd890', dk: 0.04 });
  const woman = mk(personH(257) - 1, '#c85a3a', { robe: true, hat: 'scarf', hatCol: '#f0c040', skin: '#d8a07a', rim: 0.4, rimCol: '#ffd890', dk: 0.04 });
  CAST_SPR = {
    cookA: [0, 1].map(arm => figure(cook(COOK_H), { view: 2, arm })), grill: [1, 5].map(arm => figure(cook(COOK_H, { skin: '#8a5a3a', hat: 'cap', hatCol: '#2a2a3a' }), { view: 2, arm })),
    cookB: [0, 1].map(arm => figure(cook(COOK_H, { skin: '#a8704a', beard: '#2a1a14' }), { view: 2, arm }, true)),
    story: [0, 1, 2, 4, 5].map(arm => figure(story, { view: 2, arm })),
    gA: [1, 5].map(arm => figure(gA, { view: 2, arm })), gB: [1, 5].map(arm => figure(gB, { view: 2, arm })),
    gBspin: [figure(gB, { view: 0, arm: 5 }), figure(gB, { view: 1, arm: 5 }), figure(gB, { view: 0, arm: 5 }, true), figure(gB, { view: 2, arm: 5 })],
    gBwalk: [walkSet(gB, false), walkSet(gB, true)],
    gG: [0, 1].map(arm => figure(gG, { view: 2, arm, seat: true })),
    water: [walkSet(water, false), walkSet(water, true), figure(water, { view: 2, arm: 2 }), figure(water, { view: 2, arm: 1 }), figure(water, { view: 2 })],
    tourist: [walkSet(tourist, true), walkSet(tourist, false), figure(tourist, { view: 0, arm: 1 }, true), figure(tourist, { view: 0 }, true)],
    seller: [figure(seller, { view: 0, sit: true, arm: 5 }, true), figure(seller, { view: 0, sit: true, arm: 1 }, true), figure(seller, { view: 0, arm: 2 }, true), figure(seller, { view: 0, arm: 1 }, true)],
    juice: [0, 1, 5].map(arm => figure(juice, { view: 2, arm })),
    henna: [1, 5].map(arm => figure(mk(personH(HENNA.y), '#2a2a5a', { robe: true, hat: 'scarf', hatCol: '#e8e0d0', skin: '#b87a54', rim: 0.4, dk: 0.15 }), { view: 0, sit: true, arm })),
    hennaC: figure(mk(personH(HENNA.y), '#e07aa0', { legs: '#e8e0d0', skin: '#f0c8a0', hair: '#c8a060', longHair: true, rim: 0.4, dk: 0.15 }), { view: 0, sit: true, arm: 1 }, true),
    man: [walkSet(man, true), walkSet(man, false), figure(man, { view: 0, arm: 1 })], woman: [walkSet(woman, true), walkSet(woman, false), figure(woman, { view: 0 })],
  };
}
// the diners on the grill stall's bench and the snail-eaters at the other counter, from behind: [x, stall]
const DINERS = [[22, 0], [31, 0], [43, 0], [52, 0], [64, 0], [74, 0], [86, 0], [97, 0], [326, 1], [337, 1], [350, 1], [361, 1], [394, 1]];
DINERS.forEach((D, i) => { D.rank = spread(i); });
function buildDiners() {
  const r = rng(404);
  DINERS.forEach((D, i) => {
    const S = { ...randomStyle(r, personH(BENCH) + 1 - (r() < 0.3 ? 1 : 0)), rim: 0.4, rimCol: '#ffe0a8', dk: 0.12, pack: null };
    const pose = D[1] ? {} : { seat: true };
    D.spr = [figure(S, { view: 1, ...pose }), figure(S, { view: 1, arm: 6, ...pose }), figure(S, { view: 1, arm: 5, ...pose })];
  });
}
// the storyteller's ring: [x, feet row, view (0 right, 1 back, 2 front, 3 left), squat?]
const LISTENERS = [[138, 216, 2], [147, 215, 2], [173, 215, 2], [182, 216, 2], [127, 223, 0], [194, 223, 3], [123, 231, 0], [198, 231, 3],
  [140, 238, 1, 1], [151, 239, 1, 1], [168, 239, 1, 1], [179, 238, 1, 1], [188, 241, 1]];
[9, 4, 7, 2, 1, 0, 6, 5, 3, 10, 8, 11, 12].forEach((place, i) => { LISTENERS[i].rank = (place + 0.5) / 13; });
// the gnawa's audience
const AUDIENCE = [[262, 220, 2], [306, 219, 2], [251, 228, 0], [318, 228, 3], [266, 242, 1], [278, 243, 1], [291, 242, 1], [303, 243, 1], [314, 241, 1, 0, 1]];
[6, 2, 4, 0, 1, 5, 3, 7, 8].forEach((place, i) => { AUDIENCE[i].rank = (place + 0.5) / 9; });
function buildRings() {
  const r = rng(77);
  for (const [list, cx, lightX] of [[LISTENERS, STORY.x, 150], [AUDIENCE, GNAWA.x, 284]]) list.forEach((L, i) => {
    const S = { ...randomStyle(r, personH(L[1]) + (i % 3 === 0 ? -1 : 0)), dk: 0.1, rim: 0.42, rimCol: '#ffc880' };
    if (L[3]) S.h = personH(L[1]);
    const v = L[2] === 3 ? 0 : L[2], flip = L[2] === 3, base = { view: v, squat: !!L[3] };
    L.spr = [figure(S, base, flip), figure(S, { ...base, arm: 5 }, flip), figure(S, { ...base, arm: L[4] ? 2 : 3 }, flip), figure(S, { ...base, arm: 4 }, flip)];
    L.S = S;
  });
}
// the walkers: each crosses n times a loop on its row; couples and a parent with a child share a rank
const WALKERS = [];
{
  const r = rng(1999), rows = [192, 196, 212, 214, 216, 254, 257, 261];
  let unit = -1;
  const add = (y, n, ph, dir, S, extra = {}, same = false) => { if (!same) unit++; WALKERS.push({ y, n, ph, dir, S, rank: spread(unit), ...extra }); };
  rows.forEach((y, i) => {
    const h = personH(y), near = y > 240;
    for (let k = 0; k < (near ? 2 : 3); k++) {
      const n = near ? 3 + Math.floor(r() * 3) : 2 + Math.floor(r() * 3), dir = (i + k) & 1 ? 1 : -1, ph = (k + r() * 0.6) / 3;
      add(y, n, ph, dir, { ...randomStyle(r, h + (r() < 0.3 ? -1 : 0)), dk: near ? 0.05 : 0.14, rim: 0.3 });
      const v = r(), gap = (4 + h * 0.35) / (W + 40);
      if (v < 0.25) add(y, n, ph + gap, dir, { ...randomStyle(r, h - 1), dk: near ? 0.05 : 0.14, rim: 0.3 }, {}, true);
      else if (v < 0.37) add(y, n, ph + gap, dir, { ...randomStyle(r, Math.round(h * 0.62)), robe: false, hat: null, bag: null, pack: null, dk: 0.1, rim: 0.3 }, {}, true);
    }
  });
}
function buildWalkers() { for (const w of WALKERS) w.spr = walkSet(w.S, w.dir < 0); }
// small figures in front of the far stalls and on the terraces (their ranks thin them too)
const FAR_FOLK = [];
{ const r = rng(61); for (let k = 0; k < 12; k++) FAR_FOLK.push({ y: 179 + (k % 4), n: 1 + (k % 3), ph: r(), dir: k & 1 ? 1 : -1, x0: 116, x1: 312, rank: spread(k), S: { ...randomStyle(r, 10 + (k % 2)), dk: 0.25, rim: 0.5 } }); }
const TERRACE_FOLK = [[12, 0], [24, 0], [38, 0], [66, 0], [80, 0], [92, 0], [342, 1], [356, 1], [390, 1], [404, 1], [432, 2], [446, 2], [468, 2]].map(([x, k], i) => ({ x, k, rank: spread(i + 3), col: TOPS[(i * 5) % TOPS.length], hair: i % 3 ? '#1a1214' : '#e8e0d0', ph: (i * 0.37) % 1 }));

/* ================= Per-frame: the cooks, the counters' lights, the juice cart ================= */
// where on the screen a figure's chest column and top of body are, from its feet (fx: i of the column in front)
const body = h => { const { tw, th, lh } = dims(h), x0 = -((tw - 1) >> 1); return { fx: x0 + tw, tt: -lh - th + 1 }; };
const fanUp = t => (t % FLARE) < 2;
const COOK_Y = 200, COOK_H = 20;                     // the cooks behind the counters (feet hidden)
function drawCooks(t) {
  const s = step(t, 60), hd = handAt({ h: COOK_H }), fy = COOK_Y;
  blit(CAST_SPR.cookA[hash(1, s, 3) < 0.45 ? 1 : 0], 44, fy);
  blit(CAST_SPR.cookA[hash(2, s, 3) < 0.35 ? 1 : 0], 68, fy);
  // the grill cook fans his coals with a palm-leaf fan, hard for 2 s of every 8
  const gx = 100, fl = fanUp(t), f = step(t, fl ? 2400 : 960) & 1;
  blit(CAST_SPR.grill[0], gx, fy);
  rect(ctx, gx + hd.x - 1, fy + hd.y - 1 + f * 2, 3, 2, f ? '#c8a060' : '#e0bc78'); px(ctx, gx + hd.x - 2, fy + hd.y + f, '#8a6a3a');
  // the snail man ladles broth into bowls
  const ls = step(t, 80), up = hash(5, ls, 1) < 0.5;
  blit(CAST_SPR.cookB[up ? 1 : 0], POT + 12, fy);
  line(ctx, POT + 12 - hd.x, fy + hd.y, POT + 4, up ? 182 : 186, '#a8acb8');
}
/** the far stall row's bulbs and glow (behind the square's people) */
function drawFarStallFx(t) {
  const s = step(t + 0.07, 480);
  for (const x0 of FAR_STALLS) {
    for (let x = x0 + 3; x < x0 + 18; x += 5) px(ctx, x, 166, hash(x, s >> 1, 8) < 0.12 ? '#c89050' : '#fff0c0');
    halo(HL.farStall, x0 + 10, 169, 0.8 + 0.12 * wave(t, 40 + (x0 % 7), x0 * 0.01));
  }
}
function drawStallFx(t) {
  const s = step(t + 0.07, 480);
  for (const b of BULBS) {                                // the bulbs under the eaves, one now and then a little dimmer
    const v = 0.85 + 0.15 * hash(b.k, s, 41);
    px(ctx, b.x, b.y, '#2a2024'); px(ctx, b.x, b.y + 1, '#fff6d8'); px(ctx, b.x, b.y + 2, '#ffd890');
    halo(HL.bulb, b.x, b.y + 1, v);
  }
  // the grill: coals breathing, flames licking up while he fans
  const fl = fanUp(t) ? smooth(0, 0.6, t % FLARE) * (1 - smooth(1.6, 2.6, t % FLARE)) : 0, cs = step(t, 1800);
  for (let x = STALL_A.x1 - 19; x < STALL_A.x1 - 3; x++) {
    const h = hash(x, cs, 3);
    px(ctx, x, COUNTER - 2, h < 0.3 + fl * 0.4 ? '#ffb040' : h < 0.7 ? '#e8501e' : '#7a1e10');
    if (fl > 0.2 && hash(x, cs, 4) < fl * 0.6) { px(ctx, x, COUNTER - 3, '#ffd060'); if (hash(x, cs, 5) < fl * 0.4) px(ctx, x, COUNTER - 4, '#ff7a2a'); }
  }
  halo(HL.grill, STALL_A.x1 - 11, COUNTER - 3, 0.6 + 0.35 * fl + 0.08 * hash(7, cs, 1));
  halo(HL.stall, (STALL_A.x0 + STALL_A.x1) >> 1, 184, 0.9); halo(HL.stall, (STALL_B.x0 + STALL_B.x1) >> 1, 184, 0.85);
}
/** the near stalls: the cooks inside, the counters, the grill; their smoke and steam */
function drawNearStalls(t, crowd) {
  ctx.drawImage(STALL_IN_C, 0, 0);
  if (crowd > 0) drawCooks(t);
  ctx.drawImage(STALL_OUT_C, 0, 0);
  drawStallFx(t);
  for (const P of PLUMES) drawPlume(t, P);
  drawEmbers(t, EMBERS[0]);
}
function drawJuiceSeller(t) {
  let arm = 0;
  for (let k = 0; k < 2; k++) { const u = (t + k * 12) % 24; if (u >= 4 && u < 7) arm = (step(t, 480) & 1) ? 2 : 0; else if (u >= 7 && u < 8) arm = 1; }
  blit(CAST_SPR.juice[arm === 2 ? 2 : arm], JUICE.x + 26, 233);
}
function drawJuiceCart(t) {
  ctx.drawImage(JUICE_C, JUICE.x, JUICE.y - 43);
  const s = step(t, 240);
  for (let k = 0; k < 8; k++) { const x = JUICE.x + 3 + k * 6; px(ctx, x, JUICE.y - 32, (k + s) % 7 === 0 ? '#c89050' : '#fff2c8'); }
  halo(HL.cart, JUICE.x + 24, JUICE.y - 30, 0.9 + 0.05 * wave(t, 300));
  const g = t % 6;                                        // a glint rolls over the oranges
  if (g < 1) px(ctx, JUICE.x + 4 + Math.floor(g * 10), JUICE.y - 24 + Math.floor(g * 4), '#fff4c0');
}
const JUICE_SLOT = [{ x: 64, y: 244 }, { x: 74, y: 248 }];
let JC_SPR;
function buildJuiceCustomers() {
  const r = rng(733);
  JC_SPR = JUICE_SLOT.map(sl => {
    const S = { ...randomStyle(r, personH(sl.y)), dk: 0.05, rim: 0.4, rimCol: '#ffd890', pack: null };
    return { S, inn: walkSet(S, false), out: walkSet(S, true), stand: [0, 1, 6].map(arm => figure(S, { view: 0, arm }, true)) };
  });
}
function drawJuiceCustomer(k, t, fr) {
  const u = (t + k * 12) % 24, sl = JUICE_SLOT[k], C = JC_SPR[k];
  if (u < 4) { blit(C.inn[fr], walkX(u, 0, 4, -12, sl.x), sl.y); return; }
  if (u >= 21) return;
  if (u >= 16) { blit(C.out[fr], walkX(u, 16, 21, sl.x, -12), sl.y); return; }
  const arm = u >= 8 && u < 15 ? 2 : (u >= 7 && u < 8) || (u >= 15 && u < 16) ? 1 : 0;
  blit(C.stand[arm], sl.x, sl.y);
  shadow(sl.x, sl.y);
  if (arm) { const b = body(C.S.h); px(ctx, sl.x - b.fx - (arm === 2 ? 0 : 2), sl.y + b.tt + (arm === 2 ? -1 : 2), '#f09a2a'); }   // the glass
}

/* ================= Per-frame: the henna artist under her parasol, and a visitor holding out her hand ================= */
function drawParasol(t) {
  const { x, y } = HENNA, cx = x + 5;
  for (let j = y; j > y - 19; j--) px(ctx, cx, j, '#3a3036');
  [[0, 2], [1, 5], [2, 7], [3, 8]].forEach(([j, hw]) => { for (let i = -hw; i <= hw; i++) px(ctx, cx + i, y - 22 + j, (Math.floor((i + 9) / 3) & 1) ? '#e8dcc0' : '#b83a2e'); });
  px(ctx, cx, y - 17, '#fff4d0'); halo(HL.lamp, cx, y - 16, 0.6 + 0.06 * hash(2, step(t, 1200), 1));
  rect(ctx, x - 2, y - 2, 4, 2, '#4a3020'); rect(ctx, x + 9, y - 2, 4, 2, '#4a3020');
}
function drawHenna(t, fr, crowd) {
  const { x, y } = HENNA, s = step(t, 240);
  if (seen(0.4, crowd)) blit(CAST_SPR.hennaC, x + 11, y);
  blit(CAST_SPR.henna[(s + (hash(3, s >> 2, 1) < 0.5 ? 1 : 0)) & 1], x, y);
  if (s & 1) px(ctx, x + 6, y - 9, '#5a2a14');                               // the henna cone at the visitor's palm
}

/* ================= Per-frame: the storyteller and his ring ================= */
const LAMP_ROWS = ['.k.', 'kgk', 'gwg', 'gwg', 'kgk', 'bbb'];
function drawStoryteller(t) {
  const s = step(t, 60), h = hash(11, s, 2);
  const arm = LAUGH(t) ? 3 : h < 0.3 ? 1 : h < 0.5 ? 2 : h < 0.75 ? 0 : 4;
  blit(CAST_SPR.story[arm], STORY.x, STORY.y);
  shadow(STORY.x, STORY.y, 7);
}
function drawStoryLamp(t, crowd) {
  if (crowd === 0) return;
  const x = STORY.x - 10, y = STORY.y + 1, f = 0.85 + 0.15 * hash(3, step(t, 2400), 9);
  ctx.drawImage(LAMP_C, x - 1, y - 5);
  halo(HL.lamp, x, y - 3, f); halo(HL.lampCore, x, y - 3, f);
}
function drawListener(L, i, t) {
  const laugh = LAUGH(t) && hash(i, 7, 1) < 0.85, s = step(t + i * 0.3, 960);
  let k = 0;
  if (laugh) k = (s + i) & 1 ? 1 : 0;
  else if (hash(i, step(t + i, 40), 2) < 0.12) k = 1;                      // a nod, hands together, now and then
  blit(L.spr[k], L[0], L[1] + (laugh && (s & 1) && L[2] !== 1 ? -1 : 0));
}

/* ================= Per-frame: the gnawa and their audience ================= */
function tassel(x, top, a) {                            // the gnawa cap's long tassel, whirled by the head
  const ex = x + Math.round(Math.cos(a) * 4), ey = top + Math.round(Math.sin(a) * 1.5) - 1;
  line(ctx, x, top, ex, ey, '#141018'); px(ctx, ex, ey, '#f0d890');
}
function drawGnawa(t, fr) {
  const b = beatAt(t), y = GNAWA.y, H0 = personH(y), hd = handAt({ h: H0 });
  const spinA = TAU * (40 * t / LOOP), spinB = TAU * (52 * t / LOOP + 16 * smooth(GN.a, GN.b, t));
  // left: qraqeb on every beat
  const ax = GNAWA.x - 14;
  blit(CAST_SPR.gA[b & 1], ax, y); shadow(ax, y);
  if (!(b & 1)) { px(ctx, ax + hd.x, y + hd.y, '#f0f0ff'); px(ctx, ax + hd.x, y + hd.y + 1, '#a8a8b8'); }
  tassel(ax, y - H0 - 1, spinA);
  // middle: the guembri, seated on a stool
  const gx = GNAWA.x, nod = (b >> 2) & 1;
  rect(ctx, gx - 3, y - 4, 7, 1, '#6a4428'); rect(ctx, gx - 3, y - 3, 1, 4, '#3a2416'); rect(ctx, gx + 3, y - 3, 1, 4, '#3a2416');
  rect(ctx, gx - 2, y - 3, 2, 3, '#d8d0c0'); rect(ctx, gx + 1, y - 3, 2, 3, '#c8c0b0'); px(ctx, gx - 2, y, SHOE); px(ctx, gx + 2, y, SHOE);
  blit(CAST_SPR.gG[(b >> 1) & 1], gx, y - nod);
  rect(ctx, gx, y - 9, 5, 3, '#d8c4a0'); rect(ctx, gx, y - 7, 5, 1, '#8a6a42'); px(ctx, gx + 4, y - 9, '#8a6a42');
  line(ctx, gx - 1, y - 9, gx - 8, y - 14, '#4a2a18'); px(ctx, gx - 9, y - 15, '#2a1a10'); px(ctx, gx - 7, y - 14, '#e8d8b0');
  px(ctx, gx + 2 + ((b >> 1) & 1), y - 10, '#5a3a28');
  tassel(gx, y - H0 - 1 - nod + 4, spinA + 2);
  // right: qraqeb, and in the build-up he steps out, turns and whirls his tassel
  let bx = GNAWA.x + 14, by = y, spr = CAST_SPR.gB[(b + 1) & 1];
  if (t >= GN.a + 6 && t < GN.b - 4) {
    if (t < GN.a + 10) { bx = walkX(t, GN.a + 6, GN.a + 10, GNAWA.x + 14, GNAWA.x + 6); by = Math.round(lerp(y, y + 6, (t - GN.a - 6) / 4)); spr = CAST_SPR.gBwalk[1][fr]; }
    else if (t >= GN.b - 8) { bx = walkX(t, GN.b - 8, GN.b - 4, GNAWA.x + 6, GNAWA.x + 14); by = Math.round(lerp(y + 6, y, (t - GN.b + 8) / 4)); spr = CAST_SPR.gBwalk[0][fr]; }
    else { bx = GNAWA.x + 6; by = y + 6; spr = CAST_SPR.gBspin[(b >> 1) & 3]; }
  }
  blit(spr, bx, by); shadow(bx, by);
  tassel(bx, by - H0 - 1, spinB);
}
function drawAudience(L, i, t) {
  const b = beatAt(t), g = env(t, GN.a + 4, GN.b - 2, 6);
  let k = 0;
  if (g > 0.15 && hash(i, 3, 5) < 0.3 + g) k = t > 176 && t < 192 && i % 3 === 0 ? ((b + i) & 1 ? 2 : 1) : ((b + i) & 1 ? 1 : 0);
  if (L[4] && t >= GN.a && t < GN.b) k = 2;                                  // she films the build-up
  blit(L.spr[k], L[0], L[1]);
  if (L[4] && k === 2) { const hx = L[0] + 1, hy = L[1] - L.S.h - 1; px(ctx, hx, hy, '#cfe0ff'); halo(HL.phone, hx, hy, 0.7); }
}

/* ================= Per-frame: the water seller and the visitor who photographs him ================= */
const FRINGE = ['#e83a3a', '#3ab85a', '#f0c040', '#3a7ae0', '#f070b0'];
function drawWaterSeller(t, fr, reducedMotion) {
  if (t < WATER.a || t >= WATER.b) return;
  const y = WATER.y, h = CAST_SPR.water[4].height - 10;
  let x, spr, swing = 0;
  if (t < WATER.stop) { x = walkX(t, WATER.a, WATER.stop, -12, WATER.x); spr = CAST_SPR.water[0][fr]; swing = fr & 1; }
  else if (t < WATER.go) {
    x = WATER.x;
    const ring = WATER.bells.some(b => t >= b && t < b + 1.2), pour = t >= 99.5 && t < 103;
    spr = CAST_SPR.water[ring ? 2 : pour ? 3 : 4];
    if (ring) { const hd = handAt({ h }), bx = x + hd.x - 1, by = y - h + 2 - (step(t, 1920) & 1); px(ctx, bx, by, '#ffe080'); px(ctx, bx, by + 1, '#c89030'); halo(HL.glint, bx, by, 0.8); }
  } else { x = walkX(t, WATER.go, WATER.b, WATER.x, W + 12); spr = CAST_SPR.water[0][fr]; swing = fr & 1; }
  blit(spr, x, y); shadow(x, y);
  // the big fringed hat, brass cups on his chest, the goatskin at his hip
  const top = y - h + 1;
  rect(ctx, x - 4, top, 9, 1, '#d8a040'); rect(ctx, x - 2, top - 2, 5, 2, '#e8b850'); px(ctx, x, top - 3, '#f0c860');
  for (let k = 0; k < 5; k++) px(ctx, x - 4 + k * 2, top + 1 + ((k + swing) & 1), FRINGE[k]);
  px(ctx, x - 1, top + 7, '#f0c040'); px(ctx, x + 1, top + 7, '#f0c040'); px(ctx, x, top + 8, '#ffd860');
  rect(ctx, x - 4, top + 11, 2, 4, '#6a4424');
  if (t >= 99.5 && t < 103) { const hd = handAt({ h }); px(ctx, x + hd.x, y + hd.y - 1, '#f0c040'); }
  if (t >= 100 && t < 102.5) { ctx.globalAlpha = 0.8; px(ctx, x + handAt({ h }).x + 1, y + handAt({ h }).y + 1 + (step(t, 1920) & 1), '#a8d8ff'); ctx.globalAlpha = 1; }
}
function drawTourist(t, fr, reducedMotion) {
  if (t < TOURIST.a || t >= TOURIST.b) return;
  const y = TOURIST.y;
  let x, spr;
  if (t < TOURIST.stop) { x = walkX(t, TOURIST.a, TOURIST.stop, W + 12, TOURIST.x); spr = CAST_SPR.tourist[0][fr]; }
  else if (t < TOURIST.go) { x = TOURIST.x; spr = CAST_SPR.tourist[(t >= 95 && t < 103) ? 2 : 3]; }
  else { x = walkX(t, TOURIST.go, TOURIST.b, TOURIST.x, W + 12); spr = CAST_SPR.tourist[1][fr]; }
  blit(spr, x, y); shadow(x, y);
  if (t >= 95 && t < 99.5) {                               // the camera, and its flash (a glint only with reduced motion)
    const hd = handAt({ h: spr.height - 10 }), cx = x - hd.x, cy = y + hd.y;
    rect(ctx, cx - 1, cy - 1, 2, 2, '#1a1a22');
    if (t >= TOURIST.flash && t < TOURIST.flash + 0.15) { px(ctx, cx - 1, cy - 1, '#ffffff'); if (!reducedMotion) halo(HL.flash, cx - 1, cy - 1, 0.9); }
  }
}

/* ================= Per-frame: the lantern seller, his lanterns and their stars ================= */
const lanternRot = (L, t) => TAU * (L.k * t / LOOP + L.ph);
function starDot(x, y, col, a, big) {
  x = Math.round(x); y = Math.round(y);
  ctx.globalAlpha = a; ctx.fillStyle = col; ctx.fillRect(x, y, 1, 1);
  if (big) { ctx.globalAlpha = a * 0.45; ctx.fillRect(x - 1, y, 3, 1); ctx.fillRect(x, y - 1, 1, 3); }
}
// the stars a lantern throws: on the kilim behind it and on the ground in front of the stall
function drawStars(lx, ly, rot, col, n, seed, onWall, onGround, a = 1) {
  for (let j = 0; j < n; j++) {
    const ph = TAU * j / n + hash(seed, j, 1) * 0.5, R = 7 + hash(seed, j, 2) * 9, big = hash(seed, j, 3) < 0.3;
    if (onWall) {
      const x = lx + Math.cos(ph + rot) * R, y = ly + Math.sin(ph + rot) * R * 0.8;
      if (x > 427 && y > 155 && y < 235) starDot(x, y, col, 0.45 * a, big);
    }
    if (onGround) {
      const x = lx + Math.cos(ph + rot) * R * 2.6, y = 247 + Math.sin(ph + rot) * R * 0.5 + (ly > 200 ? 4 : 0);
      if (x > 380 && x < W && y > 236) starDot(x, y, col, 0.35 * a, big);
    }
  }
  ctx.globalAlpha = 1;
}
function drawLanternsHanging(t) {
  LANTS.forEach((L, i) => {
    const sway = Math.round(wave(t, 9 + i * 2, L.ph) * 0.7), rot = lanternRot(L, t), spr = L.spr[Math.floor(frac(rot / TAU) * 8) & 1];
    const x = L.x + sway, top = L.bar + 2 + L.len;
    line(ctx, L.x, L.bar + 2, x, top - 1, '#3a2a1a');
    const cy = top + (spr.height >> 1);
    halo(HL.lanternGlow[i % HL.lanternGlow.length], x, cy, 0.85 + 0.15 * hash(i, step(t, 1200), 4));
    ctx.drawImage(spr, x - (spr.width >> 1), top);
    drawStars(x, cy, rot, L.col, 9, i + 20, true, false);
  });
}
function drawLanternGround(t) {
  LANTS.forEach((L, i) => drawStars(L.x, L.bar + L.len + 6, lanternRot(L, t), L.col, 7, i + 40, false, true));
  RUG_LANTS.forEach(([x], i) => halo(HL.rugLamp, x, LANT.rug + 3, 0.8 + 0.15 * hash(i, step(t, 1440), 6)));
}
const SELLER = { x: 458, y: 245 };
function drawLanternSeller(t) {
  const S = CAST_SPR.seller;
  let spr = S[(step(t, 80) + 1) % 3 === 0 ? 1 : 0], lifted = null;
  if (t >= SALE.at && t < SALE.go) {
    spr = t >= SALE.lift && t < SALE.give ? S[2] : t >= SALE.give ? S[3] : S[3];
    const hd = handAt({ h: personH(SELLER.y) });
    if (t >= SALE.lift && t < SALE.give) lifted = [SELLER.x - hd.x + 1, SELLER.y - personH(SELLER.y) - 4];
    else if (t >= SALE.give) lifted = [SELLER.x - hd.x - 3, SELLER.y + hd.y + 1];
  }
  blit(spr, SELLER.x, SELLER.y);
  if (!lifted && spr === S[0]) { const hd = handAt({ h: personH(SELLER.y) }); px(ctx, SELLER.x - hd.x + 1, SELLER.y + hd.y - 4, step(t, 960) & 1 ? '#ffe8a0' : '#c8902a'); }   // polishing a little lantern
  if (lifted) drawHeldLantern(lifted[0], lifted[1], t, true);
}
function drawHeldLantern(x, y, t, spin) {
  const rot = TAU * (3 * t / LOOP) + (spin ? TAU * 6 * smooth(SALE.lift, SALE.give, t) : 0);
  const spr = HELD_SPR[Math.floor(frac(rot / TAU) * 8) & 1];
  halo(HL.lanternGlow[0], x, y + 4, 1);
  ctx.drawImage(spr, Math.round(x) - 3, Math.round(y));
  drawStars(x, y + 4, rot, '#ffd890', 12, 77, x > 424, true, 1.3);
}
function drawCouple(t, fr) {
  if (t < SALE.a || t >= SALE.b) return;
  const y = 257, C = CAST_SPR;
  for (const [who, stop] of [['woman', 436], ['man', 446]]) {
    let x, spr;
    if (t < SALE.at) { x = walkX(t, SALE.a, SALE.at, W + 14 + (who === 'man' ? 8 : 0), stop); spr = C[who][0][(fr + (who === 'man' ? 1 : 0)) & 3]; }
    else if (t < SALE.go) { x = stop; spr = who === 'man' && t >= SALE.give ? C.man[2] : C[who][2]; }
    else { x = walkX(t, SALE.go, SALE.b, stop, W + 14 + (who === 'man' ? 0 : -8)); spr = C[who][1][(fr + (who === 'man' ? 1 : 0)) & 3]; }
    blit(spr, x, y); shadow(x, y);
    if (who === 'man' && t >= SALE.give + 1) { const hd = handAt({ h: personH(y) }); drawHeldLantern(x + hd.x, y + hd.y + 1, t, false); }
  }
}

/* ================= Per-frame: walkers, the far figures, the terraces ================= */
function drawWalker(w, i, t, fr) {
  const span = W + 40, u = frac(t * w.n / LOOP + w.ph), x = Math.round(w.dir > 0 ? -20 + u * span : W + 20 - u * span);
  if (x < -14 || x > W + 14) return;
  shadow(x, w.y);
  blit(w.spr[(fr + i * 3) & 3], x, w.y);
}
let FAR_SPR;
function drawFarFolk(t, fr, crowd) {
  FAR_FOLK.forEach((F, i) => {
    if (!seen(F.rank, crowd)) return;
    const u = frac(t * F.n / LOOP + F.ph), x = Math.round(F.dir > 0 ? lerp(F.x0, F.x1, u) : lerp(F.x1, F.x0, u));
    blit(FAR_SPR[i][(fr + i) & 3], x, F.y);
  });
}
function drawTerraceFolk(t, crowd) {
  for (const P of TERRACE_FOLK) {
    if (!seen(P.rank, crowd)) continue;
    const T = TERRACES[P.k], base = T.par - 3, s = step(t + P.ph * 40, 48), turn = hash(P.x, s, 2) < 0.3 ? 1 : 0;
    rect(ctx, P.x - 1, base - 3, 4, 3, tint(P.col, NIGHT, 0.3)); rect(ctx, P.x, base - 6, 2, 3, P.hair === '#e8e0d0' ? '#7a5038' : '#5a3a28');
    rect(ctx, P.x, base - 6, 2, 1, P.hair === '#e8e0d0' ? '#c8b8a0' : '#1a1214'); px(ctx, P.x + turn, base - 5, '#c88a64');
    if (hash(P.x, s, 3) < 0.25) { px(ctx, P.x + 2, base - 5, '#c88a64'); px(ctx, P.x + 2, base - 6, '#e8c070'); }   // a glass raised
  }
  if (crowd > 0) {                                        // the waiter with his tray along the left terrace
    const u = frac(t * 6 / LOOP), x = Math.round(10 + 96 * (u < 0.5 ? u * 2 : 2 - u * 2)), base = TERRACES[0].par - 3;
    rect(ctx, x - 1, base - 5, 3, 5, '#e8e4dc'); rect(ctx, x - 1, base - 8, 2, 3, '#2a1a14'); rect(ctx, x + 1, base - 7, 4, 1, '#c8c8d0');
    px(ctx, x + 2, base - 8, '#e8f0f8'); px(ctx, x + 3, base - 8, '#3ab06a');
  }
}

/* ================= Per-frame: the calèche, the moped, the cats ================= */
let VEH;
function drawCaleche(t, fr) {
  for (const C of CALECHE) {
    if (t < C.a || t >= C.b) continue;
    const x = C.dir > 0 ? walkX(t, C.a, C.b, -70, W + 6) : walkX(t, C.a, C.b, W + 6, -70), spr = VEH.cal[C.dir > 0 ? 0 : 1][fr];
    ctx.globalAlpha = 0.35; ctx.fillStyle = '#07040a'; ctx.fillRect(x + 4, LANE_V + 1, 58, 1); ctx.globalAlpha = 1;
    ctx.drawImage(spr, x, LANE_V - 32);
    const lx = C.dir > 0 ? x + 38 : x + 27;
    px(ctx, lx, LANE_V - 21, '#fff0c0'); halo(HL.carLamp, lx, LANE_V - 21, 0.8 + 0.1 * hash(3, step(t, 1200), 1));
  }
}
function drawMoped(t) {
  for (const M of MOPED) {
    if (t < M.a || t >= M.b) continue;
    const y = LANE_V + 4, x = M.dir > 0 ? walkX(t, M.a, M.b, -26, W + 4) : walkX(t, M.a, M.b, W + 4, -26);
    const spr = VEH.moped[M.dir > 0 ? 0 : 1][step(t, 2400) & 1];
    shadow(x + 11, y, 16);
    ctx.drawImage(spr, x, y - 19);
    const hx = M.dir > 0 ? x + 18 : x + 3, tx = M.dir > 0 ? x + 2 : x + 19;
    ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = 0.3;
    ctx.drawImage(VEH.beam[M.dir > 0 ? 0 : 1], M.dir > 0 ? hx : hx - VEH.beam[0].width, y - 14);
    ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
    halo(HL.head, hx, y - 11, 0.9); halo(HL.tail, tx, y - 7, 0.7);
  }
}
const CAT_SIT = ['e.e..', 'hhh..', 'hHh..', '.bbb.', 'bbbb.', 'bbbbb'];
const CAT_WALK = [['........e.e', 't......hhhh', '.tbbbbbbhH.', '..bbbbbbb..', '..b.b..b.b.'], ['........e.e', '.t.....hhhh', 't.bbbbbbhH.', '..bbbbbbb..', '...bb...bb.']];
const CAT_CURL = ['...eh.', '.bbbbhh', 'bbbbbbb', '.bbbbt.'];
let CAT_SPR;
function drawCats(t) {
  // a tabby by the grill stall's bench, waiting for scraps: the tail swishes, now and then it looks up
  const sx = 34, sy = 207, look = hash(4, step(t, 48), 1) < 0.3;
  ctx.drawImage(CAT_SPR.sit[look ? 1 : 0], sx, sy - 5);
  const tail = Math.round(wave(t, 30) * 2 + wave(t, 97, 0.3));
  for (let k = 0; k < 4; k++) px(ctx, sx + 5 + k, sy - Math.round(Math.max(0, tail * k / 4)), '#7a4a26');
  // a black cat curled up on the lantern seller's rug, breathing; an ear flicks now and then
  ctx.drawImage(CAT_SPR.curl, 468, 244);
  if (wave(t, 60) > 0.6) px(ctx, 471, 245, '#2a2430');
  if (hash(9, step(t, 160), 2) < 0.15) px(ctx, 472, 244, '#3a3440');
}
function drawRunningCat(t, fr) {
  if (t < CAT_RUN.a || t >= CAT_RUN.b) return;
  const x = walkX(t, CAT_RUN.a, CAT_RUN.b, -12, W + 4);
  ctx.drawImage(CAT_SPR.walk[(fr >> 1) & 1], x, 259 - 4);
}

/* ================= Per-frame: the sky's life ================= */
const SWIFTS = [];
{ const r = rng(88); for (let i = 0; i < 11; i++) SWIFTS.push({ cy: 46 + r() * 50, rx: 18 + r() * 34, ry: 4 + r() * 8, k: (12 + Math.floor(r() * 10)) * (r() < 0.3 ? -1 : 1), ph: r(), f: 3 + Math.floor(r() * 5) }); }
const overTower = (x, y) => (x >= KT.x0 && x <= KT.sx1 && y >= KT.wall - 4) || (x >= LT.x0 && x <= LT.sx1 && y >= 31 && y < KT.wall);
function drawSwifts(t, front) {
  for (const s of SWIFTS) {
    const a = TAU * (s.k * t / LOOP + s.ph), z = Math.sin(a);
    if ((z > 0) !== front) continue;
    const x = Math.round(231 + Math.cos(a) * s.rx), y = Math.round(s.cy + z * s.ry * 0.5 + Math.sin(a * 2) * 2);
    const col = overTower(x, y) ? '#2a1610' : '#c8a07a', up = (step(t, 2400) + s.f) & 1;
    ctx.fillStyle = col; ctx.fillRect(x, y, 1, 1);
    if (up) { ctx.fillRect(x - 1, y - 1, 1, 1); ctx.fillRect(x + 1, y - 1, 1, 1); } else { ctx.fillRect(x - 1, y, 1, 1); ctx.fillRect(x + 1, y, 1, 1); }
  }
}
const EGRET_ROWS = [['w...w', '.wbw.', '.....'], ['.....', 'wwbww', '.....'], ['.....', '.wbw.', 'w...w']];
function drawEgrets(t) {
  for (const E of EGRETS) {
    if (t < E.a || t >= E.b) continue;
    const u = (t - E.a) / (E.b - E.a), lx = E.dir > 0 ? lerp(-30, W + 30, u) : lerp(W + 30, -30, u), ly = lerp(62, 44, u);
    for (let k = 0; k < 7; k++) {
      const side = k & 1 ? 1 : -1, rank = (k + 1) >> 1, x = Math.round(lx - E.dir * rank * 7), y = Math.round(ly + side * rank * 3 + Math.sin(t * 1.3 + k) * 0.8);
      const f = Math.floor(t * 3 + k * 0.7) % 4;
      ctx.drawImage(VEH.egret[f === 3 ? 1 : f], x - 2, y - 1);
    }
  }
}
function drawPlane(t) {
  if (t < PLANE.a || t >= PLANE.b) return;
  const u = (t - PLANE.a) / (PLANE.b - PLANE.a), x = Math.round(lerp(W + 10, -10, u)), y = Math.round(lerp(18, 34, u));
  px(ctx, x, y, '#fff4e0'); halo(HL.glint, x, y, 0.4);
  if (frac(t) < 0.12) { px(ctx, x + 2, y, '#ff4a3a'); halo(HL.tail, x + 2, y, 0.5); }
  if (frac(t + 0.5) < 0.08) px(ctx, x - 2, y, '#ffffff');
}

/* ================= Smoke, embers and motes: the scene's own drifting weather, at three depths =================
   far: the high haze over the city (behind the tower), the far stalls' thin plumes and sparks; mid: the grill's big
   plume and the snail pot's steam, with sparks when the cook fans; near: smoke from a grill just out of frame on the
   left drifting across the bottom of the picture, big embers, and dust motes turning in every pool of lamplight. */
const PUFF_SETS = {
  smoke: [['#f4dcb4', '#c8a88a'], ['#b8a8b0', '#8a7e94'], ['#5e5a7c', '#46446a']],
  steam: [['#fff8ec', '#d8d0c8'], ['#c8c8d8', '#9a9ab0'], ['#6a6a8a', '#50506c']],
  near: [['#e0b88a', '#a07a5a'], ['#b89a88', '#7a6a6a'], ['#7a6a7a', '#5a5068']],
};
const PLUMES = [
  { x: 101, y: 189, n: 40, k: 24, rise: 156, drift: 52, rMax: 14, a: 1.4, jit: 18, seed: 1, set: 'smoke', flare: true },
  { x: 386, y: 181, n: 16, k: 30, rise: 84, drift: 24, rMax: 8, a: 0.9, jit: 8, seed: 2, set: 'steam' },
];
const FAR_PLUMES = FAR_STALLS.map((x0, i) => ({ x: x0 + 10, y: 162, n: 8, k: 18 + (i % 3) * 2, rise: 84, drift: 26, rMax: 7, a: 0.6, jit: 6, seed: 10 + i, set: 'smoke' }));
const NEAR_PLUME = { x: -8, y: 276, n: 14, k: 16, rise: 96, drift: 96, rMax: 13, a: 0.45, jit: 12, seed: 3, set: 'near' };
let PUFF;
function drawPlume(t, P) {
  const boost = P.flare && fanUp(t) ? 1 + 0.6 * smooth(0, 0.8, t % FLARE) : 1;
  for (let i = 0; i < P.n; i++) {
    const { cyc, u } = cycle(t, P.k, i / P.n);
    const x = P.x + (hash(P.seed, i, cyc) - 0.5) * P.jit + u * P.drift + Math.sin(u * 5 + i) * 2 * u + wave(t, 7, i * 0.13) * u * 4;
    const y = P.y - u * P.rise, r = Math.max(2, Math.min(14, Math.round(2 + u * P.rMax * (0.8 + 0.4 * hash(P.seed, i, cyc + 7)))));
    const a = P.a * Math.min(1, u * 6) * Math.pow(1 - u, 1.2) * (u < 0.3 ? boost : 1);
    if (a < 0.01) continue;
    ctx.globalAlpha = Math.min(1, a);
    ctx.drawImage(PUFF[P.set][u < 0.22 ? 0 : u < 0.55 ? 1 : 2][r], Math.round(x) - r, Math.round(y) - r);
  }
  ctx.globalAlpha = 1;
}
const EMBERS = [
  { x: 102, y: 189, n: 16, k: 40, rise: 64, spread: 12, size: 1, seed: 5, flare: true },
  { x: 8, y: 270, n: 12, k: 20, rise: 120, spread: 70, size: 2, seed: 6 },
];
function drawEmbers(t, E) {
  const fl = E.flare && fanUp(t);
  for (let i = 0; i < E.n; i++) {
    if (E.flare && !fl && i % 3) continue;                    // a few always, a burst while he fans
    const { cyc, u } = cycle(t, E.k + (i % 4), i / E.n);
    const x = E.x + (hash(E.seed, i, cyc) - 0.3) * E.spread * (0.3 + u) + Math.sin(u * 9 + i) * 2, y = E.y - u * E.rise * (0.6 + 0.4 * hash(E.seed, i, cyc + 3));
    const a = (1 - u) * (0.6 + 0.4 * hash(E.seed, i, step(t, 2400)));
    ctx.globalAlpha = a; ctx.fillStyle = u < 0.35 ? '#ffd060' : u < 0.7 ? '#ff8a2a' : '#c83a1a';
    const s = E.size > 1 && hash(E.seed, i, 9) < 0.5 ? 2 : 1;
    ctx.fillRect(Math.round(x), Math.round(y), s, s === 2 && u > 0.5 ? 1 : s);
  }
  ctx.globalAlpha = 1;
}
function drawFarEmbers(t) {
  for (let s = 0; s < FAR_STALLS.length; s++) for (let i = 0; i < 2; i++) {
    const { cyc, u } = cycle(t, 30 + s, i / 2 + s * 0.13), x = FAR_STALLS[s] + 6 + hash(s, i, cyc) * 8 + u * 5, y = 164 - u * 30;
    ctx.globalAlpha = (1 - u) * 0.8; ctx.fillStyle = u < 0.5 ? '#ffb040' : '#c8501e'; ctx.fillRect(Math.round(x), Math.round(y), 1, 1);
  }
  ctx.globalAlpha = 1;
}
// motes turning in the pools of light: [x, y, rx, ry, count, crowd-only?]
const MOTE_LIGHTS = [[30, 212, 22, 14, 8], [150, 216, 18, 12, 7, 1], [452, 206, 26, 34, 10], [62, 186, 42, 8, 8], [360, 186, 34, 8, 7], [284, 216, 18, 12, 6, 1]];
const MOTES = [];
{ const r = rng(515); MOTE_LIGHTS.forEach((L, li) => { for (let k = 0; k < L[4]; k++) MOTES.push({ li, k1: 2 + Math.floor(r() * 6), k2: 3 + Math.floor(r() * 6), p1: r(), p2: r(), b: 0.5 + r() * 0.5 }); }); }
function drawMotes(t, crowd) {
  ctx.fillStyle = '#ffe8c0';
  for (const m of MOTES) {
    const L = MOTE_LIGHTS[m.li];
    if (L[5] && crowd === 0) continue;
    const dx = Math.sin(TAU * (m.k1 * t / LOOP + m.p1)), dy = Math.sin(TAU * (m.k2 * t / LOOP + m.p2));
    const a = m.b * (1 - Math.min(1, Math.hypot(dx, dy) * 0.85)) * (0.6 + 0.4 * wave(t, 50 + m.k1, m.p2));
    if (a < 0.05) continue;
    ctx.globalAlpha = a; ctx.fillRect(Math.round(L[0] + dx * L[2]), Math.round(L[1] + dy * L[3]), 1, 1);
  }
  ctx.globalAlpha = 1;
}

/* ================= Per-frame: the tower, the houses' lights, the palms, the sign ================= */
function drawMinaret(t) {
  halo(HL.tower, 232, 102, 0.95);
  ctx.drawImage(MIN_C.gold, MIN_BOX.x, MIN_BOX.y);
  const shift = 0.5 + 0.5 * wave(t, 2, 0.15);                 // the floodlights warm towards rose and back, very slowly
  ctx.globalAlpha = shift * 0.75; ctx.drawImage(MIN_C.rose, MIN_BOX.x, MIN_BOX.y); ctx.globalAlpha = 1;
  const g = 0.6 + 0.25 * wave(t, 6, 0.3) + 0.08 * hash(1, step(t, 600), 2);   // the lantern's lamps inside
  for (const [x, y, side] of LANTERN_WIN) { ctx.globalAlpha = g * (side ? 0.6 : 1); ctx.fillStyle = y < 51 ? '#ffe8a8' : '#ffc070'; ctx.fillRect(x, y, 1, 1); }
  ctx.globalAlpha = 1;
  halo(HL.lantern, 231, 51, g);
  const gl = wave(t, 8, 0.2);                                  // a glint slides over the globes
  if (gl > 0.7) for (const [gy, r] of GLOBES) px(ctx, SP.x - 1, Math.round(gy - r * 0.4), '#fffbe0');
  halo(HL.globe, SP.x, 22, 0.6 + 0.2 * gl);
}
function drawPalms(t) {
  PALMS.forEach((P, i) => {
    const C = CROWNS[i], sway = Math.round(wave(t, 10 + i * 3, i * 0.21) * 0.8 + wave(t, 37 + i, i * 0.5) * 0.4);
    ctx.drawImage(C.c, P.x + P.lean - C.cx + sway, P.top - C.cy + 2);
  });
}
function drawTownLights(t) {
  for (const w of FAR_WIN) if (hash(w.x, Math.floor(frac(t / LOOP + w.ph) * w.n), 4) < 0.6) px(ctx, w.x, w.y, w.col);
  for (let i = 0; i < WIN.length; i++) {
    const w = WIN[i];
    if (hash(i + 50, Math.floor(frac(t / LOOP + w.ph) * w.n), 4) < 0.45) continue;
    ctx.fillStyle = w.col; ctx.fillRect(w.x + 1, w.y + 2, w.w - 2, w.h - 2); ctx.fillRect(w.x + 2, w.y + 1, w.w - 4, 1);
    ctx.fillStyle = '#3a1e16'; ctx.fillRect(w.x + 2, w.y + 2, 1, w.h - 2); ctx.fillRect(w.x + 1, w.y + 4, w.w - 2, 1);   // the window's bars
    halo(HL.win, w.x + 2, w.y + 4, 0.6);
  }
  const s = step(t + 0.11, 480);
  for (const b of STRANDS) {
    const h = hash(b.k, s, 61);
    ctx.fillStyle = h < 0.08 ? '#6a4a28' : b.c && b.k % 3 === 0 ? '#ff7a5a' : b.c && b.k % 3 === 1 ? '#6ad0ff' : b.c ? '#a8ff8a' : h < 0.2 ? '#ffffff' : '#ffe08a';
    ctx.fillRect(b.x, b.y, 1, 1);
    if (b.k % 3 === 0 && h >= 0.08) halo(HL.strand, b.x, b.y, 0.5);
  }
  for (const T of TERRACES) halo(HL.terrace, (T.x0 + T.x1) >> 1, T.par - 8, 0.85 + 0.08 * wave(t, 20, T.x0 * 0.01));
  halo(HL.door, 311, 172, 0.8);
}
function drawOwnAd(t) {
  ctx.drawImage(AD_C[Math.floor(t * 30 / LOOP + 0.3) & 1], BB.x, BB.y);
  const u = frac(t * 80 / LOOP);                               // the tea runs down the stream, steam curls off the glass
  px(ctx, BB.x + 12, BB.y + 7 + Math.floor(u * 6), '#ffe0a0');
  ctx.fillStyle = '#ffffff';
  for (let k = 0; k < 2; k++) {
    const v = frac(t * (24 + k * 6) / LOOP + k * 0.5), x = BB.x + 11 + k * 2 + Math.round(Math.sin(v * 6 + k) * 1);
    ctx.globalAlpha = 0.6 * Math.sin(Math.PI * v); ctx.fillRect(x, BB.y + 11 - Math.floor(v * 4), 1, 1);
  }
  ctx.globalAlpha = 1;
}
function drawBillboard(t, pitch) {
  halo(HL.bbSky, BB.x + (BB.w >> 1), BB.y + (BB.h >> 1), 0.8);
  ctx.drawImage(BB_FR_C, BB_FR.x, BB_FR.y);
  if (!PITCH.covers(t, pitch)) drawOwnAd(t);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = 0.1;
  ctx.drawImage(BB_WASH_C, BB.x, BB.y);
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  for (const lx of BB_LAMPS) halo(HL.bbLamp, lx, BB.y - 7, 0.8 + 0.08 * wave(t, 900 + lx));
}
function renderWash() {
  const p = painter(BB.w, BB.h);
  for (let y = 0; y < 6; y++) for (let i = 0; i < BB.w; i++) {
    const v = Math.max(...BB_LAMPS.map(lx => 1 - Math.hypot((i + BB.x - lx) / 9, y / 6)));
    if (v > bayer(i, y) * 0.9) p.put(i, y, '#fff0c8');
  }
  return p.done();
}

/* ================= A picked weather (site/js/engine/weather.js) =================
   Marrakech has no rain of its own (its drifting smoke, embers and motes stay in every weather). Far drops end on the
   houses and the far stalls and catch the floodlit tower; mid ones land on the square in the stalls' and lamps' light;
   near ones fall past everything. With nobody on the square (crowd 0) the storyteller's and the gnawa's lamps are gone. */
const WEATHER = weatherPlans([]);
const WX_LAND = [[150, 182], [196, 246], [H + 4, H + 4]];
const LIGHT_MID = [{ x: 62, y: 186, rx: 52, ry: 14, col: '#ffe0a8' }, { x: 360, y: 186, rx: 46, ry: 14, col: '#ffe0a8' },
  { x: 30, y: 212, rx: 26, ry: 18, col: '#fff0c8' }, { x: 452, y: 200, rx: 32, ry: 44, col: '#ffcf70' }];
const LIGHT_LAMPS = [{ x: 150, y: 222, rx: 20, ry: 14, col: '#ffd890' }, { x: 284, y: 222, rx: 18, ry: 12, col: '#ffd890' }];
const LIGHT_FAR = [{ x: 232, y: 104, rx: 22, ry: 58, col: '#ffd8a0' }, { x: 205, y: 170, rx: 100, ry: 10, col: '#ffe0a8' }];
const LIGHT_BB = { x: BB.x + (BB.w >> 1), y: BB.y + 4, rx: 30, ry: 14, col: '#fff0c8' };
const wxSet = (bb, lamps) => {
  const mid = lamps ? [...LIGHT_MID, ...LIGHT_LAMPS] : LIGHT_MID;
  return [bb ? [...LIGHT_FAR, LIGHT_BB] : LIGHT_FAR, mid, mid].map(lights => ({ land: WX_LAND, lights }));
};
const WX = { bb: wxSet(true, true), plain: wxSet(false, true), bbNone: wxSet(true, false), plainNone: wxSet(false, false) };

/* ================= Pools of light, the vignette ================= */
function makePool(rx, ry, col, maxA) {                 // a flattened dithered ellipse of light on the ground (drawn additively)
  const rgb = hex(col), w = rx * 2 + 1, h = ry * 2 + 1, [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    const dd = Math.hypot((i - rx) / rx, (y - ry) / ry);
    if (dd >= 1) continue;
    const q = Math.floor(Math.pow(1 - dd, 1.4) * 5 + bayer(i, y)) / 5, o = (y * w + i) * 4;
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = Math.round(Math.min(1, q) * maxA * 255);
  }
  x.putImageData(img, 0, 0);
  return { c, rx, ry };
}
function drawPool(pl, x, y, a) {
  if (a <= 0.004) return;
  ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = Math.min(1, a);
  ctx.drawImage(pl.c, Math.round(x) - pl.rx, Math.round(y) - pl.ry);
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
}
function renderVignette() {
  const [c, x] = makeCanvas(W, H), img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.8, 1.5, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q <= 0) continue;
    const o = (y * W + i) * 4; d[o] = 8; d[o + 1] = 4; d[o + 2] = 14; d[o + 3] = Math.round(q * 0.4 * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}
function renderBeam() {                                    // the moped's headlight on the dust (facing right)
  const p = painter(30, 9);
  for (let y = 0; y < 9; y++) for (let i = 0; i < 30; i++) {
    const half = 1 + i * 0.13, d = Math.abs(y - 4) / half;
    if (d < 1 && (1 - d) * (1 - i / 30) > bayer(i, y) * 0.8) p.put(i, y, '#fff0c8');
  }
  return p.done();
}

/* ================= Pre-render everything static ================= */
let HL, POOL, SKY_C, MOON_C, HAZE, FAR_C, MIN_C, HALL_C, CROWNS, TOWN_C, STALL_IN_C, STALL_OUT_C, JUICE_C, LAMP_C, LANT_BACK_C, LANT_RUG_C,
  HELD_SPR, VIG_C, BB_FR_C, BB_WASH_C, AD_C, PITCH, ACTORS;
function prerender() {
  HL = {
    moon: makeHalo(24, '#c8d0f0', 0.14, 1.5), tower: makeHalo(84, '#ffb060', 0.13, 1.5), lantern: makeHalo(10, '#ffd890', 0.4, 1.3),
    globe: makeHalo(6, '#ffd890', 0.3, 1.3), bulb: makeHalo(7, '#ffe0a0', 0.4, 1.3), farStall: makeHalo(14, '#ffc070', 0.22, 1.5),
    grill: makeHalo(12, '#ff7a2a', 0.45, 1.3), stall: makeHalo(40, '#ffc880', 0.12, 1.5), cart: makeHalo(18, '#fff0c8', 0.25, 1.4),
    lamp: makeHalo(18, '#ffc870', 0.28, 1.4), lampCore: makeHalo(4, '#fff4d0', 0.6, 1.2), glint: makeHalo(3, '#fff0c0', 0.6, 1.2),
    flash: makeHalo(14, '#ffffff', 0.9, 1.2), phone: makeHalo(3, '#cfe0ff', 0.5, 1.2), win: makeHalo(6, '#ffb060', 0.22, 1.4),
    strand: makeHalo(3, '#ffe0a0', 0.4, 1.2), terrace: makeHalo(30, '#ffb870', 0.1, 1.5), door: makeHalo(10, '#ffc070', 0.3, 1.4),
    carLamp: makeHalo(5, '#fff0c0', 0.5, 1.2), head: makeHalo(7, '#fff4d8', 0.55, 1.2), tail: makeHalo(3, '#ff3a2a', 0.6, 1.1),
    bbSky: makeHalo(30, '#ffe0a0', 0.08, 1.5), bbLamp: makeHalo(5, '#fff0c8', 0.55, 1.2), rugLamp: makeHalo(8, '#ffcf70', 0.35, 1.3),
    lanternGlow: ['#ffcf70', '#ff9a50', '#ff5a4a', '#ffe08a', '#ffcf70', '#5aa8ff', '#ffd890', '#ffb860', '#6ae08a'].map(c => makeHalo(9, c, 0.42, 1.3)),
  };
  POOL = { story: makePool(40, 12, '#ffb860', 0.32), gnawa: makePool(34, 10, '#ffb860', 0.26) };
  SKY_C = renderSky(); MOON_C = renderMoon();
  HAZE = [{ c: renderHaze(44, 1, ['#141c4a', '#1e2454'], 1.0), y: 4, k: 1 }, { c: renderHaze(58, 2.4, ['#2c2a58', '#3c3460'], 1.0), y: 94, k: 2 }];
  FAR_C = renderFarCity();
  const m = buildMinaret();
  MIN_C = { gold: paintMinaret(m, 'gold'), rose: paintMinaret(m, 'rose') };
  HALL_C = renderHallLayer(); CROWNS = PALMS.map(renderCrown); TOWN_C = renderTown();
  { const pin = painter(W, H), pout = painter(W, H), a = renderStall(pin, pout, STALL_A, 'grill'), b = renderStall(pin, pout, STALL_B, 'snail');
    STALL_IN_C = pin.done(); STALL_OUT_C = pout.done();
    text(pin.x, a.label, a.bx + 2, 178, '#f4e8c8'); text(pin.x, b.label, b.bx + 2, 178, '#f4e8c8');
    text(pout.x, STALL_A.num, a.mid - 3, 169, '#ffffff'); text(pout.x, STALL_B.num, b.mid - 3, 169, '#ffffff'); }
  JUICE_C = renderJuiceCart(); LAMP_C = sprite(LAMP_ROWS, { k: '#6a4a24', g: '#ffe8a0', w: '#ffffff', b: '#3a2a18' });
  LANT_BACK_C = renderLanternBack(); LANT_RUG_C = renderLanternRug();
  for (const L of LANTS) L.spr = lanternSprites(L);
  HELD_SPR = lanternSprites({ shape: 0, col: '#ffd890' });
  BB_FR_C = renderBillboardFrame(); BB_WASH_C = renderWash(); AD_C = ['MINT TEA', 'ATAY 4DH'].map(renderAd);
  PITCH = makePitch(BB.w, BB.h, { accent: '#e2725b' });
  const puffs = cols => { const out = [null]; for (let r = 1; r <= 14; r++) out.push(makePuff(r, cols[0], cols[1])); return out; };
  PUFF = Object.fromEntries(Object.entries(PUFF_SETS).map(([k, stages]) => [k, stages.map(puffs)]));
  VIG_C = renderVignette();
  const cal = [0, 1, 2, 3].map(makeCaleche), moped = [0, 1].map(makeMoped), beam = renderBeam();
  VEH = { cal: [cal, cal.map(flipCanvas)], moped: [moped, moped.map(flipCanvas)], beam: [beam, flipCanvas(beam)],
    egret: EGRET_ROWS.map(rows => sprite(rows, { w: '#f4ecd8', b: '#d8c8a8' })) };
  const catKey = { e: '#5a3418', h: '#a8642e', H: '#7a4420', b: '#94562a', t: '#7a4a26' };
  CAT_SPR = { sit: [sprite(CAT_SIT, catKey), sprite(CAT_SIT.map((r, j) => j === 2 ? 'hhh..' : r), { ...catKey, h: '#b87038' })],
    walk: CAT_WALK.map(rows => sprite(rows, { e: '#2a2430', h: '#3a3440', H: '#c8d050', b: '#2a2632', t: '#2a2632' })),
    curl: sprite(CAT_CURL, { e: '#2a2430', h: '#3a3440', b: '#1e1a24', t: '#2a2632' }) };
  buildCast(); buildDiners(); buildRings(); buildWalkers(); buildJuiceCustomers();
  FAR_SPR = FAR_FOLK.map(F => walkSet(F.S, F.dir < 0));
  // everyone and everything standing on the square, sorted by the row of their feet (far to near); each carries its
  // crowd rank (0: not a person, always drawn; CAST: drawn at every level but 0)
  ACTORS = [];
  const A = (y, draw, rank = 0) => ACTORS.push({ y, draw, rank });
  A(FRONT - 3, (t, fr, crowd) => drawNearStalls(t, crowd));      // in front of the far lanes: their walkers pass behind
  A(200, t => { ctx.drawImage(LANT_BACK_C, 0, 0); drawLanternsHanging(t); });
  DINERS.forEach((D, i) => A(BENCH + (D[1] ? 0 : 0.1), t => blit(D.spr[hash(i, step(t + i * 1.7, 60), 3) < 0.35 ? 1 : hash(i, step(t + i * 1.7, 60), 4) < 0.15 ? 2 : 0], D[0], BENCH), D.rank));
  A(207, drawCats);
  A(LANE_V, (t, fr) => drawCaleche(t, fr), CAST); A(LANE_V + 4, t => drawMoped(t), CAST);
  WALKERS.forEach((w, i) => A(w.y, (t, fr) => drawWalker(w, i, t, fr), w.rank));
  A(WATER.y, (t, fr, crowd, rm) => drawWaterSeller(t, fr, rm), CAST); A(TOURIST.y, (t, fr, crowd, rm) => drawTourist(t, fr, rm), CAST);
  LISTENERS.forEach((L, i) => A(L[1], t => drawListener(L, i, t), L.rank));
  A(HENNA.y - 1, drawParasol); A(HENNA.y, drawHenna, CAST);
  A(STORY.y, drawStoryteller, CAST); A(STORY.y + 1, (t, fr, crowd) => drawStoryLamp(t, crowd));
  A(GNAWA.y, drawGnawa, CAST);
  AUDIENCE.forEach((L, i) => A(L[1], t => drawAudience(L, i, t), L.rank));
  A(233, drawJuiceSeller, CAST); A(JUICE.y, drawJuiceCart);
  A(JUICE_SLOT[0].y, (t, fr) => drawJuiceCustomer(0, t, fr), CAST); A(JUICE_SLOT[1].y, (t, fr) => drawJuiceCustomer(1, t, fr), 0.5);
  A(250, () => ctx.drawImage(LANT_RUG_C, 0, 0)); A(251, drawLanternSeller, CAST);
  A(257, (t, fr) => drawCouple(t, fr), CAST); A(259, (t, fr) => drawRunningCat(t, fr));
  ACTORS.sort((a, b) => a.y - b.y);
}

/* ================= Frame compositor (back to front) ================= */
// pitch: the site's face on the sign; billboard: false leaves the sign out (the loop videos); weather: a WEATHERS pick
// (the city's own when left out); crowd: the share of the square's people to show, 0..1 (CROWD when left out)
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false, crowd: share } = {}) {
  const crowd = clamp(typeof share === 'number' && share === share ? share : CROWD, 0, 1);
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const none = crowd === 0, wx = WEATHER(weather), fr = step(t, 768) & 3;
  const wxc = WX[(billboard ? 'bb' : 'plain') + (none ? 'None' : '')];
  // the sky: stars, the crescent, the high haze of smoke drifting west, an aeroplane coming in
  ctx.drawImage(SKY_C, 0, 0);
  for (const s of STARS) {
    ctx.globalAlpha = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph)); ctx.fillStyle = s.col; ctx.fillRect(s.x, s.y, 1, 1);
    if (s.big) { ctx.globalAlpha *= 0.35; ctx.fillRect(s.x - 1, s.y, 3, 1); ctx.fillRect(s.x, s.y - 1, 1, 3); }
  }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y, 0.9);
  ctx.drawImage(MOON_C, MOON.x - MOON.r, MOON.y - MOON.r);
  for (const L of HAZE) drawScroll(L.c, 0, L.y, L.k, t);
  drawPlane(t);
  drawWeather(ctx, wx.shared, 0, t, wxc[0], reducedMotion);
  // the city and the Atlas, the Koutoubia and its swifts, the prayer hall and the palms
  ctx.drawImage(FAR_C, 0, 0);
  drawSwifts(t, false);
  drawMinaret(t);
  drawSwifts(t, true);
  drawEgrets(t);
  ctx.drawImage(HALL_C, 0, 0);
  drawPalms(t);
  // the houses round the square, their windows and terraces, the sign; the far stalls and their smoke
  ctx.drawImage(TOWN_C, 0, 0);
  drawTownLights(t);
  drawTerraceFolk(t, crowd);
  if (billboard) drawBillboard(t, pitch);
  for (const P of FAR_PLUMES) drawPlume(t, P);
  drawFarEmbers(t);
  drawFarFolk(t, fr, crowd);
  drawFarStallFx(t);
  // the square and everyone on it, the near stalls among them (those the crowd level thins away are skipped)
  drawLanternGround(t);
  if (!none) { drawPool(POOL.story, STORY.x - 8, STORY.y + 2, 0.9 + 0.1 * hash(3, step(t, 2400), 9)); drawPool(POOL.gnawa, GNAWA.x, GNAWA.y + 4, 0.85); }
  for (const a of ACTORS) if (seen(a.rank, crowd)) a.draw(t, fr, crowd, reducedMotion);
  drawMotes(t, crowd);
  drawWeather(ctx, wx.shared, 1, t, wxc[1], reducedMotion);
  // near: smoke from a grill just out of frame, big embers
  drawPlume(t, NEAR_PLUME);
  drawEmbers(t, EMBERS[1]);
  drawWeather(ctx, wx.shared, 2, t, wxc[2], reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'marrakech',
  name: 'Marrakech',
  tagline: 'Lanterns in Jemaa el-Fnaa',
  timeZone: 'Africa/Casablanca',
  accent: '#e2725b',                                   // terracotta, written out: tools/seo/seo.mjs reads it from the source
  focusX: 232,
  landmark: [KT.x0 - 4, KT.sx1 + 4],                   // the Koutoubia's minaret, with its merlons and lantern
  landmarkTop: SP.tip,                                 // the row of the spire's finial
  billboard: BB,
  crowd: CROWD,                                        // the share of the square shown by default; render(t, { crowd }) takes 0..1
  ambience: { crowd: 0.5, traffic: 0.1, wind: 0.08, crickets: 0.12 },
  events: [
    ...MOPED.map(M => ({ t: M.a + 1, type: 'horn', x: M.dir > 0 ? 60 : 420, gain: 0.12 })),   // the moped's toot as it comes in
    ...WATER.bells.map(t => ({ t, type: 'bell', x: WATER.x, gain: 0.22 })),                    // the water seller's brass bell
    { t: SALE.give, type: 'chime', x: 452, gain: 0.16 },                                      // the lantern changes hands
  ].sort((a, b) => a.t - b.t),
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
